import { AnimatePresence, motion } from 'framer-motion';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Logo from '@/components/app/Logo';
import {
  analyzeIssueAndGetAvailabilities,
  checkZipCodeServiceability,
  fetchJobsAndBusinessUnits,
  getAddressSuggestions,
  getTeamConfig,
  scheduleAppointment,
  searchCustomerByAddress,
  searchCustomerByPhone,
} from '@/utils/simple-scheduler/api';

// Add event emitter utility function
const emitAnalyticsEvent = (eventName: string) => {
  // Dispatch the event to the window with only the event name, no additional data
  const event = new CustomEvent('avoca_scheduler', {
    detail: {
      event: eventName,
    },
    bubbles: true,
  });
  window.dispatchEvent(event);

  // Add cross-frame communication to parent window for GTM integration
  if (window.parent && window.parent !== window) {
    try {
      // Send event to parent page in GTM-friendly format
      window.parent.postMessage(
        {
          type: 'gtm_event',
          event: 'avoca_scheduler',
          avoca_event_name: eventName,
        },
        '*'
      );
    } catch (e) {
      console.error('Failed to send event to parent:', e);
    }
  }

  // Log events in development environment for testing
  if (process.env.NODE_ENV === 'development') {
    console.log(`✅ Analytics Event: ${eventName}`);
  }
};

interface EnhancedSchedulingFlowProps {
  companyId: string;
  primaryColor?: string;
  onComplete?: (data: any) => void;
  apiKey?: string;
  enableAnalytics?: boolean;
  logoUrl?: string;
}

type Stage = 'issue' | 'ownership' | 'info' | 'questions' | 'scheduling';

interface CustomerInfo {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
}

interface SystemInfo {
  age: 'under10' | 'over10';
  issue: string;
  additionalAnswers: Record<string, string>; // New field to store answers to dynamic questions
}

// Add interface for customer and location data
interface CustomerDetails {
  id: string;
  name: string;
  emails?: string;
  phone?: string;
  address?: {
    id: string;
    street: string;
    unit?: string;
    city: string;
    state: string;
    zip: string;
  };
  isMember?: boolean;
  memberships?: string[];
}

interface AddressWithOccupants {
  id: string;
  street: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  occupants: CustomerDetails[];
}

// Add interface for availability data
interface Availability {
  start: string;
  end: string;
  startUtc: string;
  endUtc: string;
  businessUnitIds: number[];
  totalAvailability: number;
  openAvailability: number;
  technicians: object[];
  isAvailable: boolean;
  isExceedingIdealBookingPercentage: boolean;
  // Add new properties for parsed string response
  id?: string;
  displayText?: string;
  // Add selected property
  selected?: boolean;
  // Add new date fields
  startDate?: string;
  endDate?: string;
  // Add timeframe field
  timeframe?: string;
}

const stages: Stage[] = [
  'issue',
  'ownership',
  'info',
  'questions',
  'scheduling',
];

const stageNames = {
  issue: 'Select Issue',
  ownership: 'Verify Ownership',
  info: 'Contact Info',
  questions: 'System Details',
  scheduling: 'Schedule Time',
};

// Add phone validation function
const validatePhoneNumber = (phone: string): boolean => {
  // Remove all non-numeric characters
  const cleanPhone = phone.replace(/\D/g, '');
  // Check if it's a valid US phone number (10 digits)
  return cleanPhone.length === 10;
};

// Format phone number as user types
const formatPhoneNumber = (value: string): string => {
  const cleanPhone = value.replace(/\D/g, '');
  if (cleanPhone.length === 0) return '';
  if (cleanPhone.length <= 3) return cleanPhone;
  if (cleanPhone.length <= 6)
    return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3)}`;
  return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6, 10)}`;
};

// Add email validation function
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 1).getDay();
};

const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

const isDateDisabled = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Move ProgressTimeline outside of the main component and memoize it
const ProgressTimeline = React.memo(
  ({
    currentStage,
    stages,
    stageNames,
    primaryColor,
  }: {
    currentStage: Stage;
    stages: Stage[];
    stageNames: Record<Stage, string>;
    primaryColor: string;
  }) => {
    const currentStageIndex = stages.indexOf(currentStage);

    return (
      <div style={{ marginBottom: '32px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
            marginBottom: '12px',
          }}
        >
          {stages.map((stage, index) => {
            const isCompleted = index <= currentStageIndex;
            const isCurrent = index === currentStageIndex;

            return (
              <div
                key={stage}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1,
                  width: `${100 / stages.length}%`,
                }}
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? primaryColor : '#f3f4f6',
                    border: `2px solid ${isCompleted ? primaryColor : isCurrent ? primaryColor : '#e5e7eb'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isCompleted ? 'white' : '#6b7280',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '10px',
                    boxShadow: isCurrent
                      ? '0 2px 6px rgba(0, 0, 0, 0.1)'
                      : 'none',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {index + 1}
                </motion.div>
                <span
                  style={{
                    fontSize: '13px',
                    color: isCurrent
                      ? primaryColor
                      : isCompleted
                        ? '#4b5563'
                        : '#9ca3af',
                    fontWeight: isCurrent ? '600' : isCompleted ? '500' : '400',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {stageNames[stage]}
                </span>
              </div>
            );
          })}
          {/* Progress line */}
          <div
            style={{
              position: 'absolute',
              top: '18px',
              left: '16px',
              right: '16px',
              height: '2px',
              backgroundColor: '#e5e7eb',
              zIndex: 0,
            }}
          />
          <motion.div
            initial={{ width: '0%' }}
            animate={{
              width: (() => {
                // Stage-specific precise positioning
                switch (currentStageIndex) {
                  case 0:
                    return '0%';
                  case 1:
                    return '25%';
                  case 2:
                    return '50%';
                  case 3:
                    return '70%';
                  case 4:
                    return '100%';
                  default:
                    return '0%';
                }
              })(),
            }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              top: '18px',
              left: '16px',
              height: '2px',
              maxWidth: 'calc(100% - 32px)',
              backgroundColor: primaryColor,
              borderRadius: '1px',
              zIndex: 0,
            }}
          />
        </div>
      </div>
    );
  }
);

// Add display name
ProgressTimeline.displayName = 'ProgressTimeline';

// Add a new interface for issue types
interface IssueType {
  name: string;
  emoji: string;
}

// Add interface for additional questions from config
interface QuestionOption {
  id: string;
  label: string;
}

interface AdditionalQuestion {
  id: string;
  question: string;
  options: QuestionOption[];
  applicableIssueTypes?: string[]; // Optional property to filter questions by issue type
}

const EnhancedSchedulingFlow: React.FC<EnhancedSchedulingFlowProps> = ({
  companyId,
  primaryColor: defaultPrimaryColor = '#e05d44',
  onComplete,
  apiKey,
  enableAnalytics = true,
  logoUrl,
}) => {
  const [currentStage, setCurrentStage] = useState<Stage>('issue');
  const [selectedIssueType, setSelectedIssueType] = useState<string>('');
  const [isHomeowner, setIsHomeowner] = useState<boolean | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
  });
  const [systemInfo, setSystemInfo] = useState<SystemInfo>({
    age: 'under10',
    issue: '',
    additionalAnswers: {}, // Initialize empty answers object
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Add state for tracking if a lookup is in progress
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isAddressLookingUp, setIsAddressLookingUp] = useState(false); // New state for address lookup
  const [customerFound, setCustomerFound] = useState(false);
  const [lastLookedUpPhone, setLastLookedUpPhone] = useState('');
  // Add state to track if fields have been touched/blurred
  const [phoneFieldTouched, setPhoneFieldTouched] = useState(false);
  const [emailFieldTouched, setEmailFieldTouched] = useState(false);

  // Add state for enhanced phone lookup flow
  const [customerMatches, setCustomerMatches] = useState<CustomerDetails[]>([]);
  const [addressMatches, setAddressMatches] = useState<AddressWithOccupants[]>(
    []
  );
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerDetails | null>(null);
  const [selectedAddress, setSelectedAddress] =
    useState<AddressWithOccupants | null>(null);
  const [showCustomerConfirmation, setShowCustomerConfirmation] =
    useState(false);
  const [showAddressSelection, setShowAddressSelection] = useState(false);
  const [showCustomerSelection, setShowCustomerSelection] = useState(false);

  // Add state for customer and location IDs
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);

  // Add state for dispatch fee
  const [dispatchFee, setDispatchFee] = useState<string | null>(null);

  // Add state for jobs and business units
  const [bizUnits, setBizUnits] = useState<{ [key: string]: number }>({});
  const [jobs, setJobs] = useState<{ [key: string]: number }>({});

  // Add state for job type and business unit
  const [selectedJobType, setSelectedJobType] = useState<string>('');
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<string>('');

  // Add state for navigation direction
  const [navigationDirection, setNavigationDirection] = useState<number>(1); // 1 for forward, -1 for backward

  // Add state to track if scheduling has been completed
  const [isSchedulingCompleted, setIsSchedulingCompleted] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isSchedulingInProgress, setIsSchedulingInProgress] = useState(false);

  // Add state for problem description prefill from config
  const [problemDescriptionPrefill, setProblemDescriptionPrefill] = useState<
    string | null
  >(null);

  // Add refs to track the latest values without triggering effect re-runs
  const phoneRef = useRef('');
  const isPhoneValidRef = useRef(false);
  const currentStageRef = useRef<Stage>('issue');
  const isSchedulingCompletedRef = useRef(false);

  // Add state for availabilities
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [isLoadingAvailabilities, setIsLoadingAvailabilities] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null
  );

  const [leadTimeMinutes, setLeadTimeMinutes] = useState<number>(0);

  // Add state for selected time slot
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [selectedTimeString, setSelectedTimeString] = useState<string | null>(
    null
  );

  // Add state for address suggestions
  const [addressSuggestions, setAddressSuggestions] = useState<
    Array<{ formatted: string }>
  >([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressDropdownRef = useRef<HTMLDivElement>(null);
  const slotsRef = useRef<HTMLDivElement>(null);

  // Add state for tracking field visibility
  const [showAddressField, setShowAddressField] = useState(false);
  const [showRemainingFields, setShowRemainingFields] = useState(false);

  // Add state for address validation
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [addressFieldTouched, setAddressFieldTouched] = useState(false);

  // Add state for zip code serviceability
  const [isCheckingZip, setIsCheckingZip] = useState(false);
  const [isZipServiceable, setIsZipServiceable] = useState<boolean | null>(
    null
  );
  const [nonServiceableZipCode, setNonServiceableZipCode] = useState<
    string | null
  >(null);
  const [showServiceabilityModal, setShowServiceabilityModal] = useState(false);
  const [showServiceableMessage, setShowServiceableMessage] = useState(false);

  // Add state to track if all customer information is filled out
  const [isAllCustomerInfoFilled, setIsAllCustomerInfoFilled] = useState(false);

  // Add state for job type ID and business unit ID
  const [selectedJobTypeId, setSelectedJobTypeId] = useState<number | null>(
    null
  );
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<
    number | null
  >(null);

  // Add a new state variable for initial loading animation
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Update the state type to use the new IssueType interface
  const [issueTypes, setIssueTypes] = useState<IssueType[]>([]);

  // Add state to store and override the primary color
  const [primaryColor, setPrimaryColor] = useState<string>(defaultPrimaryColor);

  // Add refs to track if events have been fired
  const bookingStartedFired = useRef(false);
  const issueStageStartedFired = useRef(false);

  // Add state for additional questions config
  const [additionalQuestions, setAdditionalQuestions] = useState<
    AdditionalQuestion[]
  >([]);

  // Add state for timeframe hours configuration
  const [timeframeHours, setTimeframeHours] = useState({
    morning: { start: 8, end: 12 },
    afternoon: { start: 12, end: 17 },
    allday: { start: 8, end: 17 },
  });

  const [schedulerId, setSchedulerId] = useState<number | undefined>(undefined);
  const [homeownerRequired, setHomeownerRequired] = useState<boolean>(true);
  // Add a state to track if we're showing a custom All-Day label
  const [customAllDayLabel, setCustomAllDayLabel] = useState<string>('All-Day');
  const [customAllDayNote, setCustomAllDayNote] = useState<string | null>(null);

  // Add state for privacy policy URL and team name
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState<string | null>(null);
  const [teamName, setTeamName] = useState<string | null>(null);

  // Debug effect to monitor custom label changes
  useEffect(() => {
    console.log('Custom label state updated:', {
      customAllDayLabel,
      customAllDayNote,
      companyId,
      schedulerId,
      willApplyToAll: parseInt(companyId, 10) === 630 && schedulerId === 11,
    });
  }, [customAllDayLabel, customAllDayNote, companyId, schedulerId]);

  // Debug effect to monitor custom label changes
  useEffect(() => {
    console.log('Custom label state updated:', {
      customAllDayLabel,
      customAllDayNote,
      companyId,
      schedulerId,
      willApplyToAll: parseInt(companyId, 10) === 630 && schedulerId === 11,
    });
  }, [customAllDayLabel, customAllDayNote, companyId, schedulerId]);

  // Add state for customer memberships
  const [customerMemberships, setCustomerMemberships] = useState<string[]>([]);

  // Add function to emit events only if analytics are enabled
  const trackEvent = useCallback(
    (eventName: string) => {
      if (enableAnalytics) {
        emitAnalyticsEvent(eventName);
      }
    },
    [enableAnalytics]
  );

  // Helper function to handle customer memberships in a consistent way
  const handleCustomerMemberships = useCallback(
    (customer: CustomerDetails | null, source: string = 'unknown') => {
      if (!customer) {
        setCustomerMemberships([]);
        return;
      }

      try {
        if (customer.memberships && customer.memberships.length > 0) {
          setCustomerMemberships(customer.memberships);
          console.log(
            `[${source}] Setting memberships for customer ${customer.id}:`,
            customer.memberships
          );
        } else if (customer.isMember) {
          setCustomerMemberships(['Member']);
          console.log(
            `[${source}] Customer ${customer.id} has membership flag but no details`
          );
        } else {
          setCustomerMemberships([]);
          console.log(
            `[${source}] No memberships found for customer ${customer.id}`
          );
        }
      } catch (err) {
        console.error(`[${source}] Error handling memberships:`, err);
        setCustomerMemberships([]);
      }
    },
    []
  );

  // Add click handler to detect first interaction with widget
  useEffect(() => {
    const handleWidgetClick = () => {
      if (!bookingStartedFired.current) {
        trackEvent('avoca:bookingStarted');
        bookingStartedFired.current = true;

        // If we're already on the issue stage when the user clicks, fire that event too
        if (
          currentStage === 'issue' &&
          !isInitialLoading &&
          !issueStageStartedFired.current
        ) {
          trackEvent('avoca:bookingIssueStarted');
          issueStageStartedFired.current = true;
        }
      }
    };

    // Attach listener
    document.addEventListener('click', handleWidgetClick, { once: true });

    return () => {
      document.removeEventListener('click', handleWidgetClick);
    };
  }, [trackEvent, currentStage, isInitialLoading]);

  // Track stage changes, but only after booking has started
  useEffect(() => {
    // Only track stage events after the booking has been started via click
    if (
      bookingStartedFired.current &&
      !issueStageStartedFired.current &&
      currentStage === 'issue' &&
      !isInitialLoading
    ) {
      trackEvent('avoca:bookingIssueStarted');
      issueStageStartedFired.current = true;
    }
  }, [currentStage, isInitialLoading, trackEvent]);

  // Add function to check if a zip code is serviceable
  const checkZipCodeServiceabilityStatus = useCallback(
    async (zipCode: string): Promise<boolean> => {
      if (!zipCode) return false;

      setIsCheckingZip(true);
      try {
        const teamId = parseInt(companyId, 10);
        console.log(
          `Checking if zip code ${zipCode} is serviceable for team ID ${teamId}`
        );

        const isServiceable = await checkZipCodeServiceability(
          zipCode,
          teamId,
          apiKey
        );

        setIsZipServiceable(isServiceable);
        setNonServiceableZipCode(isServiceable ? '' : zipCode);

        // Track the serviceability status
        if (isServiceable) {
          trackEvent('avoca:serviceAreaValid');
        } else {
          trackEvent('avoca:serviceAreaInvalid');
        }

        // Hide remaining fields if zip code is not serviceable
        if (!isServiceable) {
          setShowRemainingFields(false);
        }
        return isServiceable;
      } catch (error) {
        console.error('Error checking zip code serviceability:', error);
        setIsZipServiceable(false);
        setShowRemainingFields(false);

        // Track error as invalid service area
        trackEvent('avoca:serviceAreaInvalid');

        return false;
      } finally {
        setIsCheckingZip(false);
      }
    },
    [
      companyId,
      apiKey,
      trackEvent,
      setIsCheckingZip,
      setIsZipServiceable,
      setNonServiceableZipCode,
      setShowRemainingFields,
    ]
  );

  // Add debounced function for address lookup
  const debouncedAddressLookup = useCallback(
    debounce(async (input: string) => {
      // Don't fetch suggestions if input is empty or too short
      if (!input || input.trim().length < 3) {
        // Clear any existing suggestions when input is empty
        if (addressSuggestions.length > 0) {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
        }
        return;
      }

      try {
        const addresses = await getAddressSuggestions(input, apiKey);
        if (addresses && addresses.length > 0) {
          setAddressSuggestions(addresses);
          setShowAddressSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      }
    }, 300),
    [apiKey, addressSuggestions.length]
  );

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        addressDropdownRef.current &&
        !addressDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAddressSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update refs when values change
  useEffect(() => {
    phoneRef.current = customerInfo.phone;
    isPhoneValidRef.current = isPhoneValid;
    currentStageRef.current = currentStage;
    isSchedulingCompletedRef.current = isSchedulingCompleted;
  }, [customerInfo.phone, isPhoneValid, currentStage, isSchedulingCompleted]);

  // Add effect to track when users leave the page - this only runs once
  useEffect(() => {
    // Store current stage in a ref so it's accessible in event handlers
    currentStageRef.current = currentStage;

    // Store phone validation state in a ref
    isPhoneValidRef.current = isPhoneValid;

    // Store phone number in a ref
    phoneRef.current = customerInfo.phone;

    // Store scheduling completion state in a ref
    isSchedulingCompletedRef.current = isSchedulingCompleted;

    let visibilityTimer: NodeJS.Timeout | null = null;
    const visibilityTimeout = 30000; // 30 seconds

    const handleBeforeUnload = () => {
      // Only track if the user has entered a valid phone number and hasn't completed scheduling
      if (
        isPhoneValidRef.current &&
        phoneRef.current &&
        !isSchedulingCompletedRef.current
      ) {
        // Use a synchronous approach for beforeunload events
        const baseUrl = window.location.hostname.includes('localhost')
          ? 'http://localhost:3000'
          : 'https://avoca-next.vercel.app';

        const url = `${baseUrl}/api/simple-scheduler/widget-dropoff`;
        const data = JSON.stringify({
          phone_number: phoneRef.current,
          company_id: companyId,
          stage: currentStageRef.current,
        });

        // Use navigator.sendBeacon which is specifically designed for beforeunload
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        }
      }
    };

    const handleVisibilityChange = () => {
      // If page becomes hidden and user has entered a valid phone number
      if (
        document.visibilityState === 'hidden' &&
        isPhoneValidRef.current &&
        phoneRef.current &&
        !isSchedulingCompletedRef.current
      ) {
        // Set a timer - if the page stays hidden for more than 30 seconds, consider it a drop-off
        visibilityTimer = setTimeout(() => {
          // Use the same direct beacon approach for consistency
          const baseUrl = window.location.hostname.includes('localhost')
            ? 'http://localhost:3000'
            : 'https://avoca-next.vercel.app';

          const url = `${baseUrl}/api/simple-scheduler/widget-dropoff`;
          const data = JSON.stringify({
            phone_number: phoneRef.current,
            company_id: companyId,
            stage: currentStageRef.current,
          });

          if (navigator.sendBeacon) {
            const blob = new Blob([data], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
          }
        }, visibilityTimeout);
      } else if (document.visibilityState === 'visible' && visibilityTimer) {
        // If the user comes back before the timeout, clear the timer
        clearTimeout(visibilityTimer);
        visibilityTimer = null;
      }
    };

    // Add event listeners for page unload/visibility change
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimer) {
        clearTimeout(visibilityTimer);
      }
    };
  }, [
    companyId,
    currentStage,
    isPhoneValid,
    customerInfo.phone,
    isSchedulingCompleted,
  ]);

  useEffect(() => {
    const TEAM_ID = parseInt(companyId, 10) || 1; // Use a default value of 1 if parsing fails

    fetchJobsAndBusinessUnits(TEAM_ID, apiKey)
      .then(({ bizUnits, jobs }) => {
        setBizUnits(bizUnits);
        setJobs(jobs);
      })
      .catch((error) => {
        console.error('Failed to fetch jobs and business units:', error);
      });

    // Fetch team configuration
    getTeamConfig(companyId, apiKey)
      .then((config) => {
        if (config) {
          if (config.scheduler_id) {
            setSchedulerId(config.scheduler_id);

            // Set custom label for teamID 630 and scheduler_id 11
            console.log('Checking custom label conditions:', {
              TEAM_ID,
              companyId,
              schedulerId: config.scheduler_id,
              condition: TEAM_ID === 630 && config.scheduler_id === 11,
            });
            if (TEAM_ID === 630 && config.scheduler_id === 11) {
              setCustomAllDayLabel('9 am - 5 pm');
              setCustomAllDayNote('anything after-hours call (910-777-3539)');
              console.log('Custom labels set!');
            }
          }

          setHomeownerRequired(config.homeowner_required !== false);
          // Set issue types from config
          if (config.issue_types && Array.isArray(config.issue_types)) {
            setIssueTypes(config.issue_types);
            console.log('Loaded custom issue types:', config.issue_types);
          }

          if (config.lead_time_minutes) {
            console.log('Loaded lead time minutes:', config.lead_time_minutes);
            setLeadTimeMinutes(config.lead_time_minutes);
          }

          // Load timeframe hours configuration if available
          if (config.timeframe_hours) {
            try {
              // Parse if it's a string, otherwise use as is
              const parsedHours =
                typeof config.timeframe_hours === 'string'
                  ? JSON.parse(config.timeframe_hours)
                  : config.timeframe_hours;

              console.log('Loaded timeframe hours:', parsedHours);
              setTimeframeHours(parsedHours);
            } catch (e) {
              console.error('Error parsing timeframe hours:', e);
            }
          }

          // Set primary color from config if available
          if (config.primary_color) {
            setPrimaryColor(config.primary_color);
            console.log('Loaded custom primary color:', config.primary_color);
          }

          // Set dispatch fee from config if available
          if (config.dispatch_fee) {
            setDispatchFee(config.dispatch_fee);
          }

          // Set problem description prefill if available
          if (config.problem_description_prefill) {
            setProblemDescriptionPrefill(config.problem_description_prefill);
          }

          // Set privacy policy URL if available
          if (config.privacy_policy_url) {
            setPrivacyPolicyUrl(config.privacy_policy_url);
          }

          // Set team name if available
          if (config.team_name) {
            setTeamName(config.team_name);
          }

          const questionConfig = config.additional_questions;

          if (questionConfig) {
            let questionsArray;

            if (
              questionConfig.additionalQuestions &&
              Array.isArray(questionConfig.additionalQuestions)
            ) {
              // Extract the array from the additionalQuestions property
              questionsArray = questionConfig.additionalQuestions;
              console.log(
                'Found questions in additionalQuestions property:',
                questionsArray
              );
            } else {
              console.log('Unknown question config format:', questionConfig);
              questionsArray = [];
            }

            // Set the questions array to state
            setAdditionalQuestions(questionsArray);

            // Initialize answers for each question with empty strings
            if (questionsArray.length > 0) {
              const initialAnswers: Record<string, string> = {};
              questionsArray.forEach((question: AdditionalQuestion) => {
                // Default to first option if available
                if (
                  question.options &&
                  Array.isArray(question.options) &&
                  question.options.length > 0
                ) {
                  initialAnswers[question.id] = question.options[0].id;
                } else {
                  initialAnswers[question.id] = '';
                }
              });

              // Update systemInfo with initial answers
              setSystemInfo((prevState) => ({
                ...prevState,
                additionalAnswers: initialAnswers,
              }));
            }
          }
        }
      })
      .catch((error) => {
        console.error('Failed to fetch team configuration:', error);
      });
  }, [companyId, apiKey, defaultPrimaryColor]);

  // Add effect to check if all customer information is filled out
  useEffect(() => {
    const isFilled =
      Boolean(customerInfo.firstName) &&
      Boolean(customerInfo.lastName) &&
      Boolean(customerInfo.phone) &&
      Boolean(customerInfo.email) &&
      Boolean(customerInfo.address) &&
      isPhoneValid &&
      isEmailValid &&
      isAddressValid;
    setIsAllCustomerInfoFilled(isFilled);
  }, [
    customerInfo.firstName,
    customerInfo.lastName,
    customerInfo.phone,
    customerInfo.email,
    customerInfo.address,
    isPhoneValid,
    isEmailValid,
    isAddressValid,
  ]);

  const currentStageIndex = stages.indexOf(currentStage);

  // Common styles
  const containerStyles: React.CSSProperties = {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '24px',
    fontFamily: 'system-ui, sans-serif',
  };

  const buttonStyles = (selected: boolean = false): React.CSSProperties => ({
    width: '100%',
    padding: '16px',
    margin: '8px 0',
    borderRadius: '12px',
    border: `2px solid ${selected ? primaryColor : '#e5e7eb'}`,
    backgroundColor: selected ? `${primaryColor}10` : 'white',
    color: selected ? primaryColor : '#374151',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
  });

  const inputStyles: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    fontSize: '16px',
    marginBottom: '16px',
    transition: 'border-color 0.2s ease',
  };

  const navigationStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '24px',
  };

  // Function to lookup customer by phone
  const lookupCustomerByPhone = useCallback(
    async (phone: string) => {
      try {
        const teamId = parseInt(companyId, 10);
        console.log(
          `Looking up customer with phone: ${phone} and teamId: ${teamId}`
        );

        const data = await searchCustomerByPhone(phone, teamId, apiKey);
        console.log('Customer lookup response:', data);

        // Reset all selection states
        setCustomerMatches([]);
        setAddressMatches([]);
        setSelectedCustomer(null);
        setSelectedAddress(null);
        setCustomerId(null);
        setLocationId(null);
        setCustomerMemberships([]);

        if (data && data.customerDetails && data.customerDetails.length > 0) {
          // Store all customer matches
          setCustomerMatches(data.customerDetails);

          // Process the addresses
          const addressMap = new Map<string, AddressWithOccupants>();

          // Group customers by address
          data.customerDetails.forEach((customer: CustomerDetails) => {
            if (customer.address) {
              const addressKey = `${customer.address.street}${customer.address.unit || ''}${customer.address.city}${customer.address.state}${customer.address.zip}`;

              if (!addressMap.has(addressKey)) {
                addressMap.set(addressKey, {
                  id: customer.address.id,
                  street: customer.address.street,
                  unit: customer.address.unit,
                  city: customer.address.city,
                  state: customer.address.state,
                  zip: customer.address.zip,
                  occupants: [],
                });
              }

              // Add this customer to the address's occupants
              const address = addressMap.get(addressKey);
              if (address) {
                address.occupants.push(customer);
              }
            }
          });

          // Convert map to array
          const addresses = Array.from(addressMap.values());
          setAddressMatches(addresses);

          // Handle different scenarios
          if (addresses.length === 1) {
            // Single address found
            const address = addresses[0];
            setSelectedAddress(address);

            if (address.occupants.length === 1) {
              // Single customer at single address - prefill information
              const occupant = address.occupants[0];
              setSelectedCustomer(occupant);

              // Parse name parts
              const nameParts = occupant.name
                ? occupant.name.split(' ')
                : ['', ''];
              const firstName = nameParts[0] || '';
              const lastName = nameParts.slice(1).join(' ') || '';

              // Get email if available
              const email = occupant.emails
                ? occupant.emails.split(',')[0].trim()
                : '';

              // Format address
              const addressStr = `${address.street}${address.unit ? ` ${address.unit}` : ''}, ${address.city}, ${address.state} ${address.zip}`;

              // Update customer info with the selected data
              setCustomerInfo((prevInfo) => ({
                ...prevInfo,
                firstName,
                lastName,
                email: email || prevInfo.email,
                address: addressStr,
              }));

              // Set email validation state
              if (email) {
                setIsEmailValid(validateEmail(email));
              }

              // Set address validation state to true since we have a valid address from the lookup
              setIsAddressValid(true);

              // Set customer and location IDs
              setCustomerId(occupant.id);
              setLocationId(address.id);

              // Find the correct customer detail with matching ID
              let customerDetail = null;
              if (
                data &&
                data.customerDetails &&
                data.customerDetails.length > 0
              ) {
                customerDetail =
                  data.customerDetails.find(
                    (detail: CustomerDetails) => detail.id === occupant.id
                  ) || data.customerDetails[0];
              }

              // Handle memberships using the helper function
              handleCustomerMemberships(customerDetail, 'phone-lookup');

              // Show all fields since customer was selected
              setShowAddressField(true);
              setShowRemainingFields(true);
            } else if (address.occupants.length > 1) {
              // See if any occupant has memberships
              const memberOccupant = address.occupants.find(
                (occ) =>
                  (occ.memberships && occ.memberships.length > 0) ||
                  occ.isMember
              );

              // If an occupant has memberships, pre-select them before showing the dialog
              if (memberOccupant) {
                console.log(
                  'Auto-selecting occupant with membership:',
                  memberOccupant.id
                );
              }

              // Multiple customers at single address - show customer selection after a small delay
              // to ensure any other UI elements have fully updated
              setTimeout(() => {
                setShowCustomerSelection(true);
              }, 300);
            }
          } else if (addresses.length > 1) {
            // Multiple addresses found - show address selection after a small delay
            // to ensure any other UI elements have fully updated
            setTimeout(() => {
              setShowAddressSelection(true);
            }, 300);
          }
        } else {
          // No customer found with this phone number
          setShowAddressField(true);
          setShowRemainingFields(false);
        }

        return data;
      } catch (error) {
        console.error('Error looking up customer:', error);
        setShowAddressField(true);
        setShowRemainingFields(false);
        return null;
      }
    },
    [companyId, apiKey]
  );

  // Function to lookup customer by address
  const lookupCustomerByAddress = useCallback(
    async (address: {
      street: string;
      unit?: string;
      city: string;
      state: string;
      zip: string;
    }) => {
      try {
        const teamId = parseInt(companyId, 10);
        console.log(
          `Looking up customer with address: ${JSON.stringify(address)} and teamId: ${teamId}`
        );

        // Set looking up state to true
        setIsAddressLookingUp(true);

        // Check zip code serviceability first
        const isServiceable = await checkZipCodeServiceabilityStatus(
          address.zip
        );

        // If zip code is not serviceable, don't proceed with customer lookup
        if (!isServiceable) {
          setIsAddressLookingUp(false);
          return null;
        }

        const data = await searchCustomerByAddress(address, teamId, apiKey);
        console.log('Customer lookup by address response:', data);

        // Reset selection states
        setSelectedCustomer(null);
        setSelectedAddress(null);
        setAddressMatches([]);
        setCustomerMatches([]);
        setCustomerMemberships([]);

        if (data && data.customerDetails && data.customerDetails.length > 0) {
          // Store customer matches
          setCustomerMatches(data.customerDetails);

          // Process the addresses - group customers by address
          const addressMap = new Map<string, AddressWithOccupants>();

          // Group customers by address (should be just one address in this case)
          data.customerDetails.forEach((customer: CustomerDetails) => {
            if (customer.address) {
              // Create a unique key for this address
              const addressKey = `${customer.address.street},${customer.address.city},${customer.address.state},${customer.address.zip}`;

              if (!addressMap.has(addressKey)) {
                addressMap.set(addressKey, {
                  id: customer.address.id,
                  street: customer.address.street,
                  unit: customer.address.unit,
                  city: customer.address.city,
                  state: customer.address.state,
                  zip: customer.address.zip,
                  occupants: [],
                });
              }

              // Add this customer to the address's occupants
              const addr = addressMap.get(addressKey);
              if (addr) {
                addr.occupants.push(customer);
              }
            }
          });

          // Convert map to array - should be just one address
          const addresses = Array.from(addressMap.values());
          setAddressMatches(addresses);

          if (addresses.length > 0) {
            const address = addresses[0];
            setSelectedAddress(address);

            if (address.occupants.length === 1) {
              // Single customer at this address - show confirmation dialog instead of auto-filling
              const occupant = address.occupants[0];
              setSelectedCustomer(occupant);
              setSelectedAddress(address);

              // Set the address ID so we can use it if the user confirms
              setLocationId(address.id);

              // Handle memberships using the helper function
              if (
                data &&
                data.customerDetails &&
                data.customerDetails.length > 0
              ) {
                const customerDetail =
                  data.customerDetails.find(
                    (detail: CustomerDetails) => detail.id === occupant.id
                  ) || data.customerDetails[0];
                handleCustomerMemberships(customerDetail, 'address-lookup');
              }

              // Show the confirmation dialog
              setTimeout(() => {
                setShowCustomerConfirmation(true);
              }, 300);

              // Don't automatically prefill the info or show the fields until user confirms
              setShowAddressField(true);
              setShowRemainingFields(false);
            } else if (address.occupants.length > 1) {
              // Multiple customers at this address - show selection after a small delay
              // to ensure any other UI elements have fully updated
              setTimeout(() => {
                setShowCustomerSelection(true);
              }, 300);
            }
          }

          // Set looking up state to false
          setIsAddressLookingUp(false);

          return data;
        } else {
          // No customers found at this address
          console.log(
            'No customers found at this address, will create new customer and location'
          );

          // Set the entered address for the new customer
          const addressStr = `${address.street}${address.unit ? ` ${address.unit}` : ''}, ${address.city}, ${address.state} ${address.zip}`;

          // Keep the address but clear name and email fields
          setCustomerInfo((prevInfo) => ({
            ...prevInfo,
            firstName: '',
            lastName: '',
            email: '',
            address: addressStr,
          }));

          // Set validation states
          setIsAddressValid(true);
          setIsEmailValid(false);

          // Show all remaining fields to create new customer
          setShowAddressField(true);
          setShowRemainingFields(true);

          // Set looking up state to false
          setIsAddressLookingUp(false);

          return null;
        }
      } catch (error) {
        console.error('Error looking up customer by address:', error);

        // Set looking up state to false
        setIsAddressLookingUp(false);

        // Show remaining fields in case of error
        setShowRemainingFields(true);

        return null;
      }
    },
    [companyId, apiKey, checkZipCodeServiceabilityStatus]
  );

  const handleNext = () => {
    // Get the index of the current stage
    const currentIndex = stages.indexOf(currentStage);
    const nextIndex = currentIndex + 1;

    // Default to forward direction
    setNavigationDirection(1);

    // Validate fields when moving from the info stage
    if (currentStage === 'info') {
      // Validate fields before proceeding from the info stage
      if (!isPhoneValid) {
        // Show error indication for phone
        setPhoneFieldTouched(true);
        return;
      }

      // If address is visible, validate it
      if (showAddressField) {
        // Ensure user has selected from dropdown by checking isAddressValid
        if (!isAddressValid) {
          setAddressFieldTouched(true);
          return;
        }

        // Also check for ZIP code serviceability
        if (isZipServiceable === false) {
          return;
        }
      }

      // Validate remaining fields if they're visible
      if (showRemainingFields) {
        // Check if all required fields are filled out
        if (
          !customerInfo.firstName ||
          !customerInfo.lastName ||
          !validateEmail(customerInfo.email)
        ) {
          // Set email validation state based on content
          setIsEmailValid(
            customerInfo.email ? validateEmail(customerInfo.email) : false
          );

          // Set all customer info as filled (which will show validation errors)
          setIsAllCustomerInfoFilled(true);
          return;
        }
      }

      // If all validations pass, track customer info completion
      trackEvent('avoca:customerInformationCompleted');
    }

    // If all validations pass, move to the next stage
    if (nextIndex < stages.length) {
      // Move to the next stage
      setCurrentStage(stages[nextIndex]);

      // Track next stage started
      if (stages[nextIndex] === 'info') {
        trackEvent('avoca:bookingIssueCompleted');
        trackEvent('avoca:customerInformationStarted');
      } else if (stages[nextIndex] === 'questions') {
        trackEvent('avoca:systemDetailsStarted');
      } else if (stages[nextIndex] === 'scheduling') {
        trackEvent('avoca:bookingScheduleSelectionStarted');
      }

      // If moving from 'questions' to 'scheduling' and we have an issue description,
      // analyze the issue and fetch availabilities in one call
      if (currentStage === 'questions' && systemInfo.issue) {
        // Track completion of system details stage
        trackEvent('avoca:systemDetailsCompleted');

        // For team ID 612 where the age question is hidden, ensure we have a default value
        if (companyId === '612' && !systemInfo.age) {
          setSystemInfo((prevState) => ({
            ...prevState,
            age: 'under10', // Set a default value
          }));
        }

        // Clear existing availabilities and set loading state
        setAvailabilities([]);
        setIsLoadingAvailabilities(true);
        setAvailabilityError(null);
        setSelectedDate(null); // Reset selected date to remove the purple box highlight

        // Use the formattedAddress for context
        const formattedAddress = customerInfo.address;

        // Get the TEAM_ID
        const TEAM_ID = parseInt(companyId, 10) || 1;

        // Call the combined function to analyze issue and get availabilities
        analyzeIssueAndGetAvailabilities(
          systemInfo.issue,
          Object.keys(bizUnits).map((name) => ({
            name,
            id: bizUnits[name],
          })),
          Object.keys(jobs).map((name) => ({
            name,
            id: jobs[name],
          })),
          TEAM_ID,
          bizUnits,
          jobs,
          apiKey,
          formattedAddress,
          systemInfo.age,
          selectedIssueType,
          systemInfo.additionalAnswers,
          additionalQuestions,
          leadTimeMinutes,
          timeframeHours,
          customerMemberships
        )
          .then(
            ({
              jobType,
              businessUnit,
              availabilities,
              jobTypeId,
              businessUnitId,
            }) => {
              console.log('Analysis and availability fetch complete:', {
                jobType,
                businessUnit,
                jobTypeId,
                businessUnitId,
                availabilitiesCount: availabilities.length,
              });

              // Add detailed logging for the date ranges
              if (availabilities.length > 0) {
                try {
                  // Use the new date format fields
                  console.log('First availability:', availabilities[0]);
                  const firstStartDate = availabilities[0]?.startDate;
                  const lastStartDate =
                    availabilities[availabilities.length - 1]?.startDate;

                  console.log(
                    'First availability date:',
                    firstStartDate || 'Not available'
                  );
                  console.log(
                    'Last availability date:',
                    lastStartDate || 'Not available'
                  );
                } catch (e) {
                  console.error('Error logging availability dates:', e);
                }
              }

              // Set the job type and business unit
              setSelectedJobType(jobType);
              setSelectedBusinessUnit(businessUnit);

              // Store the IDs
              setSelectedJobTypeId(jobTypeId);
              setSelectedBusinessUnitId(businessUnitId);

              // Set the availabilities
              if (availabilities && availabilities.length > 0) {
                const sortedAvailabilities =
                  sortAvailabilitiesByDate(availabilities);
                setAvailabilities(sortedAvailabilities);
                updateCalendarWithEarliestDate(sortedAvailabilities);
              } else {
                setAvailabilityError(
                  'No availability found for the next 2 months.'
                );
                setAvailabilities([]);
              }

              // End loading state
              setIsLoadingAvailabilities(false);
            }
          )
          .catch((error) => {
            console.error(
              'Error analyzing issue and getting availabilities:',
              error
            );
            setAvailabilityError(
              'Error retrieving availability information. Please try again.'
            );
            setAvailabilities([]);
            setIsLoadingAvailabilities(false);
          });
      }
    }
  };

  const handleBack = () => {
    const prevIndex = currentStageIndex - 1;
    if (prevIndex >= 0) {
      // Set direction to backward
      setNavigationDirection(-1);

      // Clear availability data if navigating to scheduling stage
      if (stages[prevIndex] === 'scheduling') {
        setIsLoadingAvailabilities(true);
        setAvailabilities([]);

        // Reset loading state after a brief delay
        setTimeout(() => {
          setIsLoadingAvailabilities(false);
        }, 500);
      }

      setCurrentStage(stages[prevIndex]);
    }
  };

  // Add function to handle time slot selection
  const handleTimeSlotSelect = (slotId: string, slot: Availability) => {
    setSelectedTimeSlot(slotId);

    // Check if this is team 630 with scheduler 11
    const isCustomTeam = parseInt(companyId, 10) === 630 && schedulerId === 11;

    // First check if this is a timeframe label (Morning, Afternoon, All-Day)
    const timeframeLabels = ['Morning', 'Afternoon', 'All-Day'];

    // Extract date from the selected slot for comparison
    const slotDate = slot.startDate
      ? new Date(slot.startDate).toLocaleDateString('en-US')
      : slot.startUtc
        ? new Date(slot.startUtc).toLocaleDateString('en-US')
        : null;

    console.log('Selected slot date:', slotDate);

    // If we have a timeframe, find all slots for the same day with the same timeframe
    if (
      slotDate &&
      (timeframeLabels.includes(slot.start) ||
        (slot.timeframe && timeframeLabels.includes(slot.timeframe)))
    ) {
      // For team 630/scheduler 11, always use All-Day timeframe
      const selectedTimeframe = isCustomTeam
        ? 'All-Day'
        : slot.timeframe || slot.start;
      console.log(`Finding all ${selectedTimeframe} slots for ${slotDate}`, {
        originalTimeframe: slot.timeframe || slot.start,
        selectedTimeframe,
        isCustomTeam,
      });

      // Find all slots on the same day with the same timeframe
      const sameTimeframeSlots = availabilities.filter((a) => {
        // Get date string for comparison
        const aDate = a.startDate
          ? new Date(a.startDate).toLocaleDateString('en-US')
          : a.startUtc
            ? new Date(a.startUtc).toLocaleDateString('en-US')
            : null;

        // Check if same date and same timeframe
        return (
          aDate === slotDate &&
          (a.timeframe === selectedTimeframe || a.start === selectedTimeframe)
        );
      });

      console.log(
        `Found ${sameTimeframeSlots.length} slots for ${selectedTimeframe} on ${slotDate}`,
        sameTimeframeSlots
      );

      if (sameTimeframeSlots.length > 0) {
        // Set fixed arrival window times based on timeframe
        const arrivalWindowStart = new Date(
          slot.startDate || slot.startUtc || ''
        );
        const arrivalWindowEnd = new Date(slot.endDate || slot.endUtc || '');

        // Set standard arrival window times based on timeframe
        if (selectedTimeframe === 'Morning') {
          arrivalWindowStart.setHours(timeframeHours.morning.start, 0, 0, 0);
          arrivalWindowEnd.setHours(timeframeHours.morning.end, 0, 0, 0);
        } else if (selectedTimeframe === 'Afternoon') {
          arrivalWindowStart.setHours(timeframeHours.afternoon.start, 0, 0, 0);
          arrivalWindowEnd.setHours(timeframeHours.afternoon.end, 0, 0, 0);
        } else if (selectedTimeframe === 'All-Day') {
          // For team 630/scheduler 11, use 9-5 hours instead of default
          if (isCustomTeam) {
            arrivalWindowStart.setHours(9, 0, 0, 0);
            arrivalWindowEnd.setHours(17, 0, 0, 0);
          } else {
            arrivalWindowStart.setHours(timeframeHours.allday.start, 0, 0, 0);
            arrivalWindowEnd.setHours(timeframeHours.allday.end, 0, 0, 0);
          }
        }

        // Get the actual job slot duration from the selected time slot (typically 1-2 hours)
        // This is separate from the arrival window
        const jobStartTime = new Date(slot.startDate || slot.startUtc || '');
        const jobEndTime = new Date(slot.endDate || slot.endUtc || '');
        const jobDuration = jobEndTime.getTime() - jobStartTime.getTime();

        // Create a detailed time descriptor object
        const timeDetails = {
          timeframe: selectedTimeframe,
          // Use standard arrival windows based on timeframe
          arrivalWindowStart: arrivalWindowStart.toISOString(),
          arrivalWindowEnd: arrivalWindowEnd.toISOString(),
          // Use specific slot time for job duration
          jobStartTime: jobStartTime.toISOString(),
          jobEndTime: jobEndTime.toISOString(),
          jobDurationMs: jobDuration,
          displayText: slot.displayText || '',
          slotsCount: sameTimeframeSlots.length,
        };

        console.log(
          'Using combined timeframe range with standard arrival windows:',
          timeDetails,
          {
            isCustomTeam,
            arrivalHours: {
              start: arrivalWindowStart.getHours(),
              end: arrivalWindowEnd.getHours(),
            },
          }
        );

        // Stringify the time details to pass to the API
        const timeDetailsString = JSON.stringify(timeDetails);
        setSelectedTimeString(timeDetailsString);
      } else {
        // Fallback to just the selected slot if something went wrong
        const selectedDate = new Date(slot.startDate || slot.startUtc || '');
        const year = selectedDate.getFullYear();
        const month = selectedDate.getMonth();
        const day = selectedDate.getDate();

        // Create arrival window based on timeframe
        const arrivalWindowStart = new Date(year, month, day);
        const arrivalWindowEnd = new Date(year, month, day);

        if (selectedTimeframe === 'Morning') {
          arrivalWindowStart.setHours(timeframeHours.morning.start, 0, 0, 0);
          arrivalWindowEnd.setHours(timeframeHours.morning.end, 0, 0, 0);
        } else if (selectedTimeframe === 'Afternoon') {
          arrivalWindowStart.setHours(timeframeHours.afternoon.start, 0, 0, 0);
          arrivalWindowEnd.setHours(timeframeHours.afternoon.end, 0, 0, 0);
        } else if (selectedTimeframe === 'All-Day') {
          // For team 630/scheduler 11, use 9-5 hours instead of default
          if (isCustomTeam) {
            arrivalWindowStart.setHours(9, 0, 0, 0);
            arrivalWindowEnd.setHours(17, 0, 0, 0);
          } else {
            arrivalWindowStart.setHours(timeframeHours.allday.start, 0, 0, 0);
            arrivalWindowEnd.setHours(timeframeHours.allday.end, 0, 0, 0);
          }
        }

        const timeDetails = {
          timeframe: selectedTimeframe,
          arrivalWindowStart: arrivalWindowStart.toISOString(),
          arrivalWindowEnd: arrivalWindowEnd.toISOString(),
          jobStartTime: selectedDate.toISOString(),
          jobEndTime: new Date(
            selectedDate.getTime() + 2 * 60 * 60 * 1000
          ).toISOString(), // Default 2-hour job
          jobDurationMs: 2 * 60 * 60 * 1000, // 2 hours in milliseconds
          displayText: slot.displayText || '',
          slotsCount: 1,
        };

        console.log('Fallback to standard timeframe windows:', timeDetails);

        // Stringify the time details
        const timeDetailsString = JSON.stringify(timeDetails);
        setSelectedTimeString(timeDetailsString);
      }
    } else {
      // Handle specific time slots (not timeframe-based)
      const selectedDate = new Date(slot.startDate || slot.start || '');
      const jobEndTime = new Date(slot.endDate || slot.endUtc || '');

      // For team 630/scheduler 11, force All-Day timeframe with 9-5 arrival window
      if (isCustomTeam) {
        const year = selectedDate.getFullYear();
        const month = selectedDate.getMonth();
        const day = selectedDate.getDate();

        const arrivalWindowStart = new Date(year, month, day, 9, 0, 0, 0);
        const arrivalWindowEnd = new Date(year, month, day, 17, 0, 0, 0);

        // Create a time descriptor with All-Day timeframe
        const timeDetails = {
          timeframe: 'All-Day',
          arrivalWindowStart: arrivalWindowStart.toISOString(),
          arrivalWindowEnd: arrivalWindowEnd.toISOString(),
          jobStartTime: arrivalWindowStart.toISOString(),
          jobEndTime: arrivalWindowEnd.toISOString(),
          jobDurationMs:
            arrivalWindowEnd.getTime() - arrivalWindowStart.getTime(),
          displayText: customAllDayLabel,
          slotsCount: 1,
        };

        console.log(
          'Using All-Day timeframe for custom team (specific slot conversion):',
          timeDetails
        );

        // Stringify the time details
        const timeDetailsString = JSON.stringify(timeDetails);
        setSelectedTimeString(timeDetailsString);
      } else {
        // For specific time slots, use the same time for both job and arrival window
        // But limit arrival window to 1 hour
        const arrivalWindowStart = new Date(selectedDate);
        const arrivalWindowEnd = new Date(selectedDate);
        arrivalWindowEnd.setHours(arrivalWindowEnd.getHours() + 1); // 1-hour arrival window

        // Create a time descriptor
        const timeDetails = {
          timeframe: 'Specific',
          arrivalWindowStart: arrivalWindowStart.toISOString(),
          arrivalWindowEnd: arrivalWindowEnd.toISOString(),
          jobStartTime: selectedDate.toISOString(),
          jobEndTime: jobEndTime.toISOString(),
          jobDurationMs: jobEndTime.getTime() - selectedDate.getTime(),
          displayText: slot.displayText || '',
          formattedTime: selectedDate.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          }),
          slotsCount: 1,
        };

        console.log('Using specific time slot:', timeDetails);

        // Stringify the time details
        const timeDetailsString = JSON.stringify(timeDetails);
        setSelectedTimeString(timeDetailsString);
      }
    }
  };

  const handleScheduleAppointment = async () => {
    try {
      // Set loading state
      setIsSchedulingInProgress(true);

      // Mark scheduling as completed before making the API call
      setIsSchedulingCompleted(true);

      // Find the selected time slot
      const selectedSlot = availabilities.find(
        (a) => a.id === selectedTimeSlot
      );

      // Create a copy of systemInfo with modified issue that includes the dispatch fee
      const submissionSystemInfo = {
        ...systemInfo,
        issue: dispatchFee
          ? `${systemInfo.issue}\n\nDispatch fee stated to user: ${dispatchFee} \n\n`
          : systemInfo.issue,
      };

      const scheduleData = {
        companyId,
        issueType: selectedIssueType,
        customerInfo,
        systemInfo: submissionSystemInfo,
        selectedDate,
        selectedTime: selectedTimeString,
        jobType: selectedJobType,
        businessUnit: selectedBusinessUnit,
        // Include IDs directly - this is the key fix!
        jobTypeId: selectedJobTypeId,
        businessUnitId: selectedBusinessUnitId,
        // Include customer ID and location ID if available
        customerId: customerId || undefined,
        locationId: locationId || undefined,
        // Include the additional questions configuration for proper formatting
        additionalQuestions: additionalQuestions,
        schedulerId, // Add the scheduler_id here
        isHomeowner, // Add the homeowner flag
        customerMemberships:
          customerMemberships.length > 0 ? customerMemberships : undefined, // Include membership information
      };

      console.log('Scheduling appointment with data:', {
        ...scheduleData,
        systemInfo: {
          ...scheduleData.systemInfo,
          additionalAnswers: scheduleData.systemInfo.additionalAnswers,
        },
        additionalQuestions,
      });

      const data = await scheduleAppointment(scheduleData, apiKey);

      // Track booking completion
      trackEvent('avoca:bookingCompletion');

      // Show success animation
      setShowSuccessAnimation(true);

      // End loading state
      setIsSchedulingInProgress(false);

      if (onComplete) {
        onComplete(data);
      }
    } catch (err) {
      console.error('Error scheduling appointment:', err);
      // Reset states on error
      setIsSchedulingCompleted(false);
      setIsSchedulingInProgress(false);
      // TODO: Add error handling UI
      throw err;
    }
  };

  const renderStage = () => {
    // Animation variants for page transitions
    // When going forward (direction > 0): content slides from right to left
    // When going backward (direction < 0): content slides from left to right
    const slideVariants = {
      enter: (direction: number) => ({
        x: direction > 0 ? 1000 : -1000,
        opacity: 0,
      }),
      center: {
        x: 0,
        opacity: 1,
      },
      exit: (direction: number) => ({
        x: direction < 0 ? 1000 : -1000,
        opacity: 0,
      }),
    };

    // If scheduling is completed, show the success animation
    if (showSuccessAnimation) {
      return (
        <motion.div
          key='success'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          style={{
            padding: '48px 24px',
            textAlign: 'center',
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: 'spring',
              damping: 10,
              stiffness: 200,
              delay: 0.2,
            }}
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              color: 'white',
              fontSize: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            ✓
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{
              color: '#111827',
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '16px',
            }}
          >
            Appointment Scheduled!
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            style={{
              color: '#6b7280',
              fontSize: '16px',
            }}
          >
            We&apos;ll be in touch soon to confirm your appointment details.
          </motion.p>
        </motion.div>
      );
    }

    const content = () => {
      switch (currentStage) {
        case 'issue':
          return (
            <div>
              <h2
                style={{
                  marginBottom: '24px',
                  color: '#111827',
                  fontSize: '24px',
                }}
              >
                What issue are you experiencing?
              </h2>
              {issueTypes.map((type) => (
                <motion.button
                  key={type.name}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedIssueType(type.name);
                    // Auto-advance to the next stage when an issue type is selected
                    setTimeout(() => handleNext(), 300);
                  }}
                  style={buttonStyles(selectedIssueType === type.name)}
                >
                  <span style={{ marginRight: '12px', fontSize: '24px' }}>
                    {type.emoji}
                  </span>
                  {type.name}
                </motion.button>
              ))}
            </div>
          );

        case 'ownership':
          return (
            <div>
              <h2
                style={{
                  marginBottom: '24px',
                  color: '#111827',
                  fontSize: '24px',
                }}
              >
                Are you the homeowner?
              </h2>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setIsHomeowner(true);
                  // Auto-advance to the next stage when homeowner is selected
                  setTimeout(() => handleNext(), 300);
                }}
                style={buttonStyles(isHomeowner === true)}
              >
                <span style={{ marginRight: '12px', fontSize: '24px' }}>
                  🏠
                </span>
                Yes, I am the homeowner
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setIsHomeowner(false);
                  // Track when user selects they are not the homeowner
                  trackEvent('avoca:notHomeownerSelected');
                  // Auto-advance to the next stage when not homeowner is selected (if homeowner is not required)
                  if (!homeownerRequired) {
                    setTimeout(() => handleNext(), 300);
                  }
                }}
                style={buttonStyles(isHomeowner === false)}
              >
                <span style={{ marginRight: '12px', fontSize: '24px' }}>
                  👥
                </span>
                No, I am not the homeowner
              </motion.button>
              {isHomeowner === false && homeownerRequired && (
                <div
                  style={{
                    marginTop: '20px',
                    padding: '16px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '8px',
                    color: '#991b1b',
                  }}
                >
                  ⚠️ We can&apos;t schedule a consultation without the homeowner
                  present. Please have the homeowner contact us directly.
                </div>
              )}
              <div style={navigationStyles}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                  }}
                >
                  Back
                </motion.button>
              </div>
            </div>
          );

        case 'info':
          return (
            <div>
              <h2
                style={{
                  marginBottom: '24px',
                  color: '#111827',
                  fontSize: '24px',
                }}
              >
                Your Contact Information
              </h2>
              <div style={{ marginBottom: '24px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#4b5563',
                  }}
                >
                  Phone Number *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type='tel'
                    value={customerInfo.phone}
                    onChange={(e) => {
                      const value = e.target.value;
                      const formattedPhone = formatPhoneNumber(value);
                      const isValid = validatePhoneNumber(formattedPhone);

                      setCustomerInfo({
                        ...customerInfo,
                        phone: formattedPhone,
                        address: '',
                        firstName: '',
                        lastName: '',
                        email: '',
                      });

                      setIsPhoneValid(isValid);

                      // Reset customer found state when phone number changes
                      setCustomerFound(false);

                      // Reset customer and location IDs
                      setCustomerId(null);
                      setLocationId(null);

                      console.log('Phone number input:', formattedPhone);
                      console.log(
                        'Phone number (digits only):',
                        formattedPhone.replace(/\D/g, '')
                      );

                      // Reset zip code serviceability state
                      setIsZipServiceable(true);
                      setNonServiceableZipCode('');

                      // Reset address validation state
                      setIsAddressValid(false);

                      setShowRemainingFields(false);

                      if (isValid && !showAddressField) {
                        // Don't immediately show additional fields - wait for lookup to complete
                        setIsLookingUp(true);

                        // Update the last looked up phone number
                        setLastLookedUpPhone(formattedPhone);

                        lookupCustomerByPhone(formattedPhone)
                          .then((result) => {
                            setCustomerFound(
                              result &&
                                result.customerDetails &&
                                result.customerDetails.length > 0
                            );
                          })
                          .finally(() => {
                            setIsLookingUp(false);
                          });
                      }
                    }}
                    // Add onBlur handler to initiate lookup when user clicks out of the field
                    onBlur={() => {
                      // Mark the field as touched when user clicks out
                      setPhoneFieldTouched(true);

                      // Only perform lookup if the phone number is valid, we're not already looking up,
                      // and the phone number has changed since the last lookup
                      if (
                        isPhoneValid &&
                        !isLookingUp &&
                        customerInfo.phone !== lastLookedUpPhone
                      ) {
                        setIsLookingUp(true);

                        // Update the last looked up phone number
                        setLastLookedUpPhone(customerInfo.phone);

                        lookupCustomerByPhone(customerInfo.phone)
                          .then((result) => {
                            setCustomerFound(
                              result &&
                                result.customerDetails &&
                                result.customerDetails.length > 0
                            );
                          })
                          .finally(() => {
                            setIsLookingUp(false);
                          });
                      }
                    }}
                    onKeyDown={(e) => {
                      // If the user presses Enter, treat it like a blur event
                      if (e.key === 'Enter') {
                        e.preventDefault(); // Prevent form submission

                        setPhoneFieldTouched(true);

                        if (isPhoneValid && !isLookingUp) {
                          setIsLookingUp(true);

                          // Update the last looked up phone number
                          setLastLookedUpPhone(customerInfo.phone);

                          lookupCustomerByPhone(customerInfo.phone)
                            .then((result) => {
                              setCustomerFound(
                                result &&
                                  result.customerDetails &&
                                  result.customerDetails.length > 0
                              );
                            })
                            .finally(() => {
                              setIsLookingUp(false);
                            });
                        }
                      }
                    }}
                    style={{
                      ...inputStyles,
                      paddingRight: '40px',
                      borderColor:
                        customerInfo.phone && phoneFieldTouched
                          ? isPhoneValid
                            ? '#10b981' // Green border for valid input
                            : '#ef4444' // Red border for invalid input
                          : '#e5e7eb', // Default border when empty or not yet touched
                    }}
                    placeholder='(555) 555-5555'
                    required
                  />
                  <AnimatePresence key='phone-validation'>
                    {customerInfo.phone &&
                      phoneFieldTouched &&
                      !isLookingUp &&
                      !isPhoneValid && <></>}
                    {isLookingUp && (
                      <motion.div
                        key='loading-spinner'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '20px',
                          marginRight: '8px',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            border: `2px solid ${primaryColor}`,
                            borderTopColor: 'transparent',
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                {customerInfo.phone && phoneFieldTouched && !isPhoneValid && (
                  <motion.p
                    key='phone-error-message'
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={{
                      color: '#ef4444',
                      fontSize: '14px',
                      marginTop: '4px',
                    }}
                  >
                    Please enter a valid 10-digit phone number
                  </motion.p>
                )}
              </div>

              <AnimatePresence key='address-fields'>
                {showAddressField && (
                  <motion.div
                    key='address-field'
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <div style={{ marginBottom: '24px' }}>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          color: '#4b5563',
                        }}
                      >
                        Service Address *
                      </label>
                      <div
                        style={{
                          position: 'relative',
                          marginBottom:
                            showAddressSuggestions &&
                            addressSuggestions.length > 0
                              ? '160px'
                              : '0',
                        }}
                        ref={addressDropdownRef}
                      >
                        <input
                          type='text'
                          value={customerInfo.address}
                          onChange={(e) => {
                            const newAddress = e.target.value;
                            setCustomerInfo({
                              ...customerInfo,
                              address: newAddress,
                              firstName: '',
                              lastName: '',
                              email: '',
                              // Keep the phone number
                            });

                            // Reset customer and location IDs
                            setSelectedCustomer(null);
                            setCustomerId(null);
                            setLocationId(null);

                            // Reset zip code serviceability state
                            setIsZipServiceable(true);
                            setNonServiceableZipCode('');

                            // Reset address validation state
                            setIsAddressValid(false);

                            // Hide remaining fields until we validate the address
                            setShowRemainingFields(false);

                            debouncedAddressLookup(newAddress);
                          }}
                          onBlur={() => {
                            setAddressFieldTouched(true);

                            // If address field has content but isn't validated (not selected from dropdown)
                            // just show error message - don't attempt manual lookup
                            if (customerInfo.address && !isAddressValid) {
                              setShowAddressField(true);
                              // Don't show remaining fields
                              setShowRemainingFields(false);
                            }
                          }}
                          onFocus={() => {
                            if (addressSuggestions.length > 0) {
                              setShowAddressSuggestions(true);
                            }
                          }}
                          onKeyDown={(e) => {
                            // If the user presses Enter, treat it like a blur event
                            if (e.key === 'Enter') {
                              e.preventDefault(); // Prevent form submission
                              setAddressFieldTouched(true);

                              // Don't try manual lookup - simply show validation error if needed
                              if (customerInfo.address && !isAddressValid) {
                                setShowAddressField(true);
                                setShowRemainingFields(false);
                              }
                            }
                          }}
                          style={{
                            ...inputStyles,
                            marginBottom: 0,
                            borderColor:
                              customerInfo.address && addressFieldTouched
                                ? isAddressValid && isZipServiceable !== false
                                  ? '#10b981' // Green border only for valid AND serviceable address
                                  : isZipServiceable === false
                                    ? '#ef4444' // Red border for non-serviceable address
                                    : '#ef4444' // Red border for invalid address
                                : '#e5e7eb', // Default border when empty or not yet touched
                          }}
                          placeholder='Enter your service address'
                          required
                        />
                        <AnimatePresence key='address-validation'>
                          {customerInfo.address &&
                            addressFieldTouched &&
                            !isAddressValid && <></>}
                          {isAddressLookingUp && (
                            <motion.div
                              key='address-loading-spinner'
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '20px',
                                marginRight: '8px',
                              }}
                            >
                              <div
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  border: `2px solid ${primaryColor}`,
                                  borderTopColor: 'transparent',
                                  animation: 'spin 1s linear infinite',
                                }}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {customerInfo.address &&
                          addressFieldTouched &&
                          !isAddressValid && (
                            <motion.p
                              key='address-error-message'
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              style={{
                                color: '#ef4444',
                                fontSize: '14px',
                                marginTop: '4px',
                              }}
                            >
                              Please select a valid address from the dropdown
                              suggestions
                            </motion.p>
                          )}
                        {showAddressSuggestions &&
                          addressSuggestions.length > 0 && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                marginTop: '4px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 10,
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                              }}
                            >
                              {addressSuggestions.map((suggestion, index) => (
                                <div
                                  key={index}
                                  onClick={async () => {
                                    const addressParts =
                                      suggestion.formatted.split(',');
                                    if (addressParts.length >= 3) {
                                      const street = addressParts[0].trim();
                                      const city = addressParts[1].trim();
                                      const stateZipParts = addressParts[2]
                                        .trim()
                                        .split(' ');
                                      const state = stateZipParts[0];
                                      const zip = stateZipParts[1];

                                      // Set the address in state
                                      setCustomerInfo({
                                        ...customerInfo,
                                        address: suggestion.formatted,
                                      });
                                      setShowAddressSuggestions(false);
                                      setIsAddressValid(true);

                                      // Hide remaining fields while looking up
                                      setShowRemainingFields(false);

                                      // Check zip code serviceability
                                      const isServiceable =
                                        await checkZipCodeServiceabilityStatus(
                                          zip
                                        );

                                      // Only proceed with customer lookup if zip code is serviceable
                                      if (isServiceable) {
                                        // Look up customer by address
                                        await lookupCustomerByAddress({
                                          street,
                                          unit: '',
                                          city,
                                          state,
                                          zip,
                                        });
                                      }
                                    }
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom:
                                      index < addressSuggestions.length - 1
                                        ? '1px solid #e5e7eb'
                                        : 'none',
                                    fontSize: '14px',
                                    color: '#374151',
                                    transition: 'background-color 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      'white';
                                  }}
                                >
                                  {suggestion.formatted}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {showRemainingFields && !isLookingUp && (
                  <motion.div
                    key='remaining-fields'
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        marginBottom: '16px',
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: '#4b5563',
                          }}
                        >
                          First Name *
                        </label>
                        <input
                          type='text'
                          value={customerInfo.firstName}
                          onChange={(e) =>
                            setCustomerInfo({
                              ...customerInfo,
                              firstName: e.target.value,
                            })
                          }
                          style={inputStyles}
                          required
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: 'block',
                            marginBottom: '8px',
                            color: '#4b5563',
                          }}
                        >
                          Last Name *
                        </label>
                        <input
                          type='text'
                          value={customerInfo.lastName}
                          onChange={(e) =>
                            setCustomerInfo({
                              ...customerInfo,
                              lastName: e.target.value,
                            })
                          }
                          style={inputStyles}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          color: '#4b5563',
                        }}
                      >
                        Email Address *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type='email'
                          value={customerInfo.email}
                          onChange={(e) => {
                            const email = e.target.value;
                            setCustomerInfo({
                              ...customerInfo,
                              email,
                            });
                            setIsEmailValid(validateEmail(email));
                          }}
                          onBlur={() => {
                            setEmailFieldTouched(true);
                          }}
                          style={{
                            ...inputStyles,
                            marginBottom: 0,
                            paddingRight: '40px',
                            borderColor:
                              customerInfo.email && emailFieldTouched
                                ? isEmailValid
                                  ? '#10b981' // Green border for valid input
                                  : '#ef4444' // Red border for invalid input
                                : '#e5e7eb', // Default border when empty or not yet touched
                          }}
                          placeholder='you@example.com'
                          required
                        />
                        <AnimatePresence key='email-validation'>
                          {customerInfo.email &&
                            emailFieldTouched &&
                            !isEmailValid && <></>}
                        </AnimatePresence>
                      </div>
                      {customerInfo.email &&
                        emailFieldTouched &&
                        !isEmailValid && (
                          <motion.p
                            key='email-error-message'
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            style={{
                              color: '#ef4444',
                              fontSize: '14px',
                              marginTop: '4px',
                            }}
                          >
                            Please enter a valid email address
                          </motion.p>
                        )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Add SMS disclaimer before the navigation buttons */}
              {currentStage === 'info' && (
                <div
                  style={{
                    marginTop: '16px',
                    marginBottom: '16px',
                    fontSize: '8px',
                    lineHeight: '1.4',
                    color: '#6b7280',
                    padding: '10px',
                    borderRadius: '6px',
                  }}
                >
                  By putting in your number, you agree to receive text messages
                  or calls at the number provided related to your request,
                  appointment updates and notifications, including messages sent
                  by the autodialer. Consent is not a condition of purchase.
                  Message and Data Rates may apply. Message frequency varies.
                  You may unsubscribe to stop receiving further messages at any
                  time by replying STOP. Text HELP for customer care
                  information.
                </div>
              )}

              {/* Add privacy policy link if available */}
              {currentStage === 'info' && privacyPolicyUrl && teamName && (
                <div
                  style={{
                    marginTop: '8px',
                    marginBottom: '16px',
                    fontSize: '12px',
                    textAlign: 'center',
                    color: '#6b7280',
                  }}
                >
                  <a
                    href={privacyPolicyUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      color: primaryColor,
                      textDecoration: 'underline',
                      fontWeight: '500',
                    }}
                  >
                    {teamName} Privacy Policy
                  </a>
                </div>
              )}

              <div style={navigationStyles}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                  }}
                >
                  Back
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleNext}
                  disabled={
                    !customerInfo.firstName ||
                    !customerInfo.lastName ||
                    !customerInfo.phone ||
                    !customerInfo.email ||
                    !customerInfo.address ||
                    !isPhoneValid ||
                    !isEmailValid ||
                    !isAddressValid
                  }
                  style={{
                    padding: '12px 24px',
                    backgroundColor: primaryColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    opacity:
                      customerInfo.firstName &&
                      customerInfo.lastName &&
                      customerInfo.phone &&
                      customerInfo.email &&
                      customerInfo.address &&
                      isPhoneValid &&
                      isEmailValid &&
                      isAddressValid
                        ? 1
                        : 0.5,
                  }}
                >
                  Continue
                </motion.button>
              </div>
            </div>
          );

        case 'questions':
          return (
            <div>
              <h2
                style={{
                  marginBottom: '24px',
                  color: '#111827',
                  fontSize: '24px',
                }}
              >
                Additional Information
              </h2>

              {/* Render dynamic additional questions from config - with updated filtering logic */}
              {additionalQuestions && additionalQuestions.length > 0
                ? additionalQuestions
                    .filter(
                      (question) =>
                        // Show question if:
                        // 1. It has no applicableIssueTypes property (applies to all)
                        // 2. It has applicableIssueTypes that includes the selected issue type
                        !question.applicableIssueTypes ||
                        question.applicableIssueTypes.includes(
                          selectedIssueType
                        )
                    )
                    .map((question) => {
                      console.log('Rendering question:', question);
                      return (
                        <div key={question.id} style={{ marginBottom: '24px' }}>
                          <label
                            style={{
                              display: 'block',
                              marginBottom: '12px',
                              color: '#4b5563',
                            }}
                          >
                            {question.question}
                          </label>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            {question.options?.map((option) => (
                              <motion.button
                                key={option.id}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() =>
                                  handleAdditionalQuestionAnswer(
                                    question.id,
                                    option.id
                                  )
                                }
                                style={{
                                  ...buttonStyles(
                                    systemInfo.additionalAnswers[
                                      question.id
                                    ] === option.id
                                  ),
                                  flex: 1,
                                }}
                              >
                                {option.label}
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      );
                    })
                : null}
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#4b5563',
                  }}
                >
                  Please describe the issue you&apos;re experiencing:
                </label>
                <textarea
                  value={systemInfo.issue}
                  onChange={(e) =>
                    setSystemInfo({ ...systemInfo, issue: e.target.value })
                  }
                  style={{
                    ...inputStyles,
                    height: '120px',
                    resize: 'vertical',
                  }}
                  placeholder={problemDescriptionPrefill || ''}
                />
              </div>
              <div style={navigationStyles}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                  }}
                >
                  Back
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleNext}
                  disabled={!systemInfo.issue}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: primaryColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: systemInfo.issue ? 'pointer' : 'not-allowed',
                    opacity: systemInfo.issue ? 1 : 0.5,
                    fontSize: '16px',
                    fontWeight: '500',
                  }}
                >
                  Continue
                </motion.button>
              </div>
            </div>
          );

        case 'scheduling':
          const today = new Date();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth();
          const daysInMonth = getDaysInMonth(currentYear, currentMonth);
          const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);

          // Verify we're correctly extracting the month from currentDate
          console.log('Current date object:', currentDate);
          console.log('Extracted month and year:', {
            month: currentMonth,
            monthName: MONTHS[currentMonth],
            year: currentYear,
          });

          // Get unique days with available slots
          const availableDays = availabilities.reduce(
            (days: number[], slot) => {
              try {
                // Use the startDate field if available, fall back to start
                const dateString = slot.startDate || slot.start;
                if (!dateString) {
                  console.warn('Missing date in slot:', slot);
                  return days;
                }

                // Safely create a date object with validation
                const date = new Date(dateString);

                // Validate the date is a valid date
                if (isNaN(date.getTime())) {
                  console.warn('Invalid date in availabilities:', dateString);
                  return days;
                }

                // Only include days from the current month being viewed
                if (
                  date.getMonth() === currentMonth &&
                  date.getFullYear() === currentYear
                ) {
                  const day = date.getDate();
                  if (!days.includes(day)) {
                    days.push(day);
                  }
                }
              } catch (error) {
                console.error('Error processing date in availableDays:', error);
              }
              return days;
            },
            [] as number[]
          );

          console.log(
            'Current calendar view:',
            currentYear,
            MONTHS[currentMonth]
          );
          console.log(
            'Filtered availabilities for current month:',
            availabilities.filter((slot) => {
              try {
                // Use the startDate field if available, fall back to start
                const dateString = slot.startDate || slot.start;
                if (!dateString) {
                  return false;
                }

                const date = new Date(dateString);

                // Validate the date is a valid date
                if (isNaN(date.getTime())) {
                  console.warn('Invalid date in filter:', dateString);
                  return false;
                }

                return (
                  date.getMonth() === currentMonth &&
                  date.getFullYear() === currentYear
                );
              } catch (error) {
                console.error('Error filtering availability by date:', error);
                return false;
              }
            }).length
          );

          // Function to check if a day has available slots
          const isDayAvailable = (day: number) => {
            return availableDays.includes(day);
          };

          // Function to get available slots for a specific day
          const getSlotsForDay = (day: number) => {
            const slots = availabilities.filter((slot) => {
              try {
                // Use the startDate field if available, fall back to start
                const dateString = slot.startDate || slot.start;
                if (!dateString) {
                  return false;
                }

                const date = new Date(dateString);

                // Validate the date is a valid date
                if (isNaN(date.getTime())) {
                  return false;
                }

                // Match both day, month and year to ensure month-awareness
                return (
                  date.getDate() === day &&
                  date.getMonth() === currentMonth &&
                  date.getFullYear() === currentYear
                );
              } catch (error) {
                console.error('Error in getSlotsForDay:', error);
                return false;
              }
            });
            console.log(
              `Slots for ${day} ${MONTHS[currentMonth]} ${currentYear}:`,
              slots.length
            );
            return slots;
          };

          // Function to check if a month has any available dates
          const hasAvailableDatesInMonth = (year: number, month: number) => {
            return availabilities.some((slot) => {
              try {
                // Use the startDate field if available, fall back to start
                const dateString = slot.startDate || slot.start;
                if (!dateString) {
                  return false;
                }

                const date = new Date(dateString);

                // Validate the date is a valid date
                if (isNaN(date.getTime())) {
                  return false;
                }

                return date.getMonth() === month && date.getFullYear() === year;
              } catch (error) {
                console.error('Error in hasAvailableDatesInMonth:', error);
                return false;
              }
            });
          };

          // Check if next month has any available dates
          const nextMonth = (currentMonth + 1) % 12;
          const nextMonthYear =
            currentMonth === 11 ? currentYear + 1 : currentYear;
          const hasNextMonthAvailability = hasAvailableDatesInMonth(
            nextMonthYear,
            nextMonth
          );

          const goToPreviousMonth = () => {
            const newDate = new Date(currentDate);
            newDate.setMonth(currentDate.getMonth() - 1);
            const today = new Date();
            // Only block if we're trying to go to a month before the current month
            if (
              newDate.getFullYear() < today.getFullYear() ||
              (newDate.getFullYear() === today.getFullYear() &&
                newDate.getMonth() < today.getMonth())
            ) {
              return;
            }
            setCurrentDate(newDate);
            // Reset selected date and time slot when changing months
            setSelectedDate(null);
            setSelectedTimeSlot(null);
          };

          const goToNextMonth = () => {
            // Only allow going to next month if it has available dates
            if (hasNextMonthAvailability) {
              const newDate = new Date(currentDate);
              newDate.setMonth(currentDate.getMonth() + 1);
              setCurrentDate(newDate);
              // Reset selected date and time slot when changing months
              setSelectedDate(null);
              setSelectedTimeSlot(null);
            }
          };

          const handleDateSelect = (day: number) => {
            if (isDayAvailable(day)) {
              // Create a date object for the selected day
              const date = new Date(currentYear, currentMonth, day);
              setSelectedDate(date);

              // Clear any previously selected time slot
              setSelectedTimeSlot(null);

              // We don't need to set loading state since data is already loaded
              // setIsLoadingAvailabilities(true);
              // setTimeout(() => {
              //   setIsLoadingAvailabilities(false);
              // }, 500);

              // Scroll to the slots section smoothly
              setTimeout(() => {
                slotsRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }
          };

          return (
            <div>
              <h2
                style={{
                  marginBottom: '24px',
                  color: '#111827',
                  fontSize: '24px',
                }}
              >
                Choose a Day for Your Appointment
              </h2>

              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '12px',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                }}
              >
                {/* Month Navigation */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={goToPreviousMonth}
                    style={{
                      padding: '4px 8px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      color:
                        currentDate.getMonth() === today.getMonth()
                          ? '#d1d5db'
                          : '#374151',
                      fontSize: '16px',
                    }}
                    disabled={currentDate.getMonth() === today.getMonth()}
                  >
                    ←
                  </motion.button>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111827',
                    }}
                  >
                    {MONTHS[currentMonth]} {currentYear}
                  </h3>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={goToNextMonth}
                    style={{
                      padding: '4px 8px',
                      border: 'none',
                      background: 'none',
                      cursor: hasNextMonthAvailability
                        ? 'pointer'
                        : 'not-allowed',
                      color: hasNextMonthAvailability ? '#374151' : '#d1d5db',
                      fontSize: '16px',
                    }}
                    disabled={!hasNextMonthAvailability}
                  >
                    →
                  </motion.button>
                </div>

                {/* Calendar Grid */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '4px',
                    marginBottom: '12px',
                  }}
                >
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                    <div
                      key={`day-${index}`}
                      style={{
                        textAlign: 'center',
                        padding: '4px',
                        color: '#4b5563',
                        fontWeight: '600',
                        fontSize: '12px',
                      }}
                    >
                      {day}
                    </div>
                  ))}

                  {/* Empty cells for days before the first day of the month */}
                  {Array.from({ length: firstDayOfMonth }).map((_, index) => (
                    <div key={`empty-${index}`} />
                  ))}

                  {/* Calendar days */}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = new Date(currentYear, currentMonth, day);
                    const isDisabled = isDateDisabled(date);
                    const hasAvailability = isDayAvailable(day);
                    const isSelected =
                      selectedDate && isSameDay(date, selectedDate);

                    return (
                      <motion.button
                        key={day}
                        whileHover={
                          !isDisabled && hasAvailability
                            ? { scale: 1.05 }
                            : undefined
                        }
                        whileTap={
                          !isDisabled && hasAvailability
                            ? { scale: 0.95 }
                            : undefined
                        }
                        onClick={() => handleDateSelect(day)}
                        style={{
                          padding: '4px',
                          border: isSelected
                            ? `1px solid ${primaryColor}`
                            : 'none',
                          borderRadius: '4px',
                          backgroundColor: isSelected
                            ? `${primaryColor}10`
                            : 'transparent',
                          cursor:
                            isDisabled || !hasAvailability
                              ? 'not-allowed'
                              : 'pointer',
                          color: isDisabled
                            ? '#9ca3af'
                            : !hasAvailability
                              ? '#d1d5db'
                              : isSelected
                                ? primaryColor
                                : '#374151',
                          fontWeight:
                            isSelected || hasAvailability ? '600' : '400',
                          opacity: isDisabled
                            ? 0.5
                            : !hasAvailability
                              ? 0.7
                              : 1,
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          height: '28px',
                          width: '28px',
                          margin: '0 auto',
                        }}
                        disabled={isDisabled || !hasAvailability}
                      >
                        {day}
                        {hasAvailability && !isDisabled && !isSelected && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '0px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '3px',
                              height: '3px',
                              borderRadius: '50%',
                              backgroundColor: primaryColor,
                            }}
                          />
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Display time slots for selected date if available */}
              {selectedDate && !isLoadingAvailabilities && (
                <div
                  style={{
                    marginTop: '16px',
                  }}
                >
                  <div
                    ref={slotsRef}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      gap: '8px',
                      marginTop: '8px',
                    }}
                  >
                    {(() => {
                      let daySlots = getSlotsForDay(selectedDate.getDate());

                      // For team 630 with scheduler 11, only show one slot
                      if (
                        parseInt(companyId, 10) === 630 &&
                        schedulerId === 11 &&
                        daySlots.length > 0
                      ) {
                        // Keep only the first slot
                        daySlots = [daySlots[0]];
                        console.log(
                          'Filtered to single slot for team 630/scheduler 11'
                        );
                      }

                      console.log('All slots for selected day:', {
                        selectedDate: selectedDate.getDate(),
                        totalSlots: daySlots.length,
                        slots: daySlots,
                        companyId,
                        schedulerId,
                        customLabels: { customAllDayLabel, customAllDayNote },
                      });
                      return daySlots;
                    })()
                      .sort((a, b) => {
                        try {
                          // First sort by timeframe (Morning, Afternoon, All-Day)
                          const timeframeOrder: Record<string, number> = {
                            'Early Morning': 1,
                            Morning: 2,
                            Afternoon: 3,
                            Evening: 4,
                            Night: 5,
                            'All-Day': 6,
                          };

                          const getTimeframeValue = (
                            slot: Availability
                          ): number => {
                            if (
                              slot.timeframe &&
                              timeframeOrder[slot.timeframe]
                            ) {
                              return timeframeOrder[slot.timeframe];
                            }
                            if (
                              typeof slot.start === 'string' &&
                              ['Morning', 'Afternoon', 'All-Day'].includes(
                                slot.start
                              )
                            ) {
                              return timeframeOrder[slot.start];
                            }
                            return 999; // Default high value for unknown timeframes
                          };

                          const orderA = getTimeframeValue(a);
                          const orderB = getTimeframeValue(b);

                          if (orderA !== orderB) {
                            return orderA - orderB;
                          }

                          // If timeframes are the same, fall back to time-based sorting
                          const dateStringA = a.startDate || a.start;
                          const dateStringB = b.startDate || b.start;

                          // Create date objects for comparison
                          const dateA = new Date(dateStringA);
                          const dateB = new Date(dateStringB);

                          // Validate the dates are valid
                          if (
                            isNaN(dateA.getTime()) ||
                            isNaN(dateB.getTime())
                          ) {
                            return 0; // Keep order unchanged if either date is invalid
                          }

                          return dateA.getTime() - dateB.getTime();
                        } catch (error) {
                          console.error('Error sorting time slots:', error);
                          return 0;
                        }
                      })
                      .map((slot, index) => {
                        try {
                          // Get the time text - prefer timeframe, then start, then calculate from date
                          let timeText = 'Time unavailable';

                          // For team 630 with scheduler 11, always use custom label
                          const isCustomTeam =
                            parseInt(companyId, 10) === 630 &&
                            schedulerId === 11;

                          if (slot.timeframe) {
                            const shouldUseCustomLabel =
                              parseInt(companyId, 10) === 630 &&
                              schedulerId === 11;
                            console.log('Checking custom label display:', {
                              timeframe: slot.timeframe,
                              companyId,
                              parsedCompanyId: parseInt(companyId, 10),
                              schedulerId,
                              shouldUseCustomLabel,
                              customAllDayLabel,
                            });
                            timeText = shouldUseCustomLabel
                              ? customAllDayLabel
                              : slot.timeframe;
                          } else if (
                            typeof slot.start === 'string' &&
                            slot.start !== ''
                          ) {
                            // If start is a formatted timeframe (Morning, Afternoon, All-Day), use it
                            if (
                              ['Morning', 'Afternoon', 'All-Day'].includes(
                                slot.start
                              )
                            ) {
                              timeText =
                                parseInt(companyId, 10) === 630 &&
                                schedulerId === 11
                                  ? customAllDayLabel
                                  : slot.start;
                            } else {
                              // Otherwise it might be a formatted time string
                              timeText = slot.start;
                            }
                          } else if (slot.startDate) {
                            // For team 630 with scheduler 11, always use custom label
                            if (
                              parseInt(companyId, 10) === 630 &&
                              schedulerId === 11
                            ) {
                              timeText = customAllDayLabel;
                            } else {
                              // Calculate timeframe from the date
                              try {
                                const date = new Date(slot.startDate);
                                const hour = date.getHours();

                                if (hour >= 7 && hour < 12) {
                                  timeText = 'Morning';
                                } else if (hour >= 12 && hour < 17) {
                                  timeText = 'Afternoon';
                                } else {
                                  timeText = 'All-Day';
                                }
                              } catch (dateError) {
                                console.error('Error parsing date:', dateError);
                                timeText = 'Time unavailable';
                              }
                            }
                          }

                          // Final fallback: if team 630/scheduler 11 and still no proper timeText, use custom label
                          if (
                            isCustomTeam &&
                            (timeText === 'Time unavailable' || !timeText)
                          ) {
                            timeText = customAllDayLabel;
                          }

                          const isSelected = selectedTimeSlot === slot.id;

                          return (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <motion.button
                                key={slot.id || `slot-${index}`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() =>
                                  handleTimeSlotSelect(
                                    slot.id || `slot-${index}`,
                                    slot
                                  )
                                }
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: isSelected
                                    ? `${primaryColor}20`
                                    : 'white',
                                  color: isSelected ? primaryColor : '#333',
                                  border: `1px solid ${isSelected ? primaryColor : '#e5e7eb'}`,
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: isSelected ? '600' : '500',
                                  textAlign: 'center',
                                  transition: 'all 0.2s ease',
                                  minWidth: '85px',
                                }}
                              >
                                {timeText}
                              </motion.button>
                              {(() => {
                                const shouldShowNote =
                                  parseInt(companyId, 10) === 630 &&
                                  schedulerId === 11 &&
                                  customAllDayNote;
                                console.log('Note display check:', {
                                  slot,
                                  timeframe: slot.timeframe,
                                  start: slot.start,
                                  companyId,
                                  parsedCompanyId: parseInt(companyId, 10),
                                  schedulerId,
                                  customAllDayNote,
                                  shouldShowNote,
                                });
                                return shouldShowNote ? (
                                  <div
                                    style={{
                                      fontSize: '11px',
                                      color: '#666',
                                      marginTop: '4px',
                                      maxWidth: '120px',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {customAllDayNote}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          );
                        } catch (error) {
                          console.error('Error handling time slot:', error);
                          return null;
                        }
                      })}
                  </div>
                </div>
              )}

              {/* Add loading indicator for availabilities */}
              {isLoadingAvailabilities && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '24px',
                    padding: '12px',
                    backgroundColor: `${primaryColor}10`,
                    borderRadius: '8px',
                    color: primaryColor,
                  }}
                >
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: `2px solid ${primaryColor}`,
                      borderTopColor: 'transparent',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px',
                    }}
                  />
                  <span>Loading availability information...</span>
                </div>
              )}

              {/* Display error message if there is one */}
              {!isLoadingAvailabilities && availabilityError && (
                <div
                  style={{
                    marginTop: '24px',
                    padding: '12px',
                    backgroundColor: '#fee2e2',
                    borderRadius: '8px',
                    color: '#991b1b',
                  }}
                >
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                    {availabilityError}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (systemInfo.issue) {
                        // Clear existing availabilities and set loading state
                        setAvailabilities([]);
                        setIsLoadingAvailabilities(true);
                        setAvailabilityError(null);

                        // Set up API call
                        const TEAM_ID = parseInt(companyId, 10) || 1;

                        // Call the combined function to analyze issue and get availabilities
                        analyzeIssueAndGetAvailabilities(
                          systemInfo.issue,
                          Object.keys(bizUnits).map((name) => ({
                            name,
                            id: bizUnits[name],
                          })),
                          Object.keys(jobs).map((name) => ({
                            name,
                            id: jobs[name],
                          })),
                          TEAM_ID,
                          bizUnits,
                          jobs,
                          apiKey,
                          customerInfo.address,
                          systemInfo.age,
                          selectedIssueType,
                          systemInfo.additionalAnswers,
                          additionalQuestions,
                          leadTimeMinutes,
                          timeframeHours,
                          customerMemberships
                        )
                          .then(
                            ({
                              jobType,
                              businessUnit,
                              availabilities,
                              jobTypeId,
                              businessUnitId,
                            }) => {
                              // Set the job type and business unit
                              setSelectedJobType(jobType);
                              setSelectedBusinessUnit(businessUnit);

                              // Store the IDs
                              setSelectedJobTypeId(jobTypeId);
                              setSelectedBusinessUnitId(businessUnitId);

                              // Set the availabilities
                              if (availabilities && availabilities.length > 0) {
                                const sortedAvailabilities =
                                  sortAvailabilitiesByDate(availabilities);
                                setAvailabilities(sortedAvailabilities);
                                updateCalendarWithEarliestDate(
                                  sortedAvailabilities
                                );
                              } else {
                                setAvailabilityError(
                                  'No availability found for the next 2 months.'
                                );
                                setAvailabilities([]);
                              }

                              // End loading state
                              setIsLoadingAvailabilities(false);
                            }
                          )
                          .catch((error) => {
                            console.error(
                              'Error analyzing issue and getting availabilities:',
                              error
                            );
                            setAvailabilityError(
                              'Error retrieving availability information. Please try again.'
                            );
                            setAvailabilities([]);
                            setIsLoadingAvailabilities(false);
                          });
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#991b1b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    Retry
                  </motion.button>
                </div>
              )}

              {/* Display dispatch fee notification if a time slot is selected */}
              {selectedTimeSlot && dispatchFee && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    marginTop: '24px',
                    padding: '12px 16px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${primaryColor}`,
                    color: '#4b5563',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke={primaryColor}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ marginRight: '12px', flexShrink: 0 }}
                  >
                    <circle cx='12' cy='12' r='10'></circle>
                    <line x1='12' y1='8' x2='12' y2='12'></line>
                    <line x1='12' y1='16' x2='12.01' y2='16'></line>
                  </svg>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>
                    {dispatchFee}
                  </p>
                </motion.div>
              )}

              <div style={navigationStyles}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBack}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                  }}
                >
                  Back
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleScheduleAppointment}
                  disabled={
                    !selectedDate || !selectedTimeSlot || isSchedulingInProgress
                  }
                  style={{
                    padding: '12px 24px',
                    backgroundColor: primaryColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor:
                      selectedDate &&
                      selectedTimeSlot &&
                      !isSchedulingInProgress
                        ? 'pointer'
                        : 'not-allowed',
                    opacity:
                      selectedDate &&
                      selectedTimeSlot &&
                      !isSchedulingInProgress
                        ? 1
                        : 0.5,
                    fontSize: '16px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isSchedulingInProgress ? (
                    <>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          border: '2px solid white',
                          borderTopColor: 'transparent',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px',
                        }}
                      />
                      Scheduling...
                    </>
                  ) : (
                    'Schedule Appointment'
                  )}
                </motion.button>
              </div>
            </div>
          );
      }
    };

    // Return the animated content directly without the loading overlay
    return (
      <AnimatePresence
        mode='wait'
        custom={navigationDirection}
        key='main-content'
      >
        <motion.div
          key={currentStage}
          custom={navigationDirection}
          variants={slideVariants}
          initial='enter'
          animate='center'
          exit='exit'
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        >
          {content()}
        </motion.div>
      </AnimatePresence>
    );
  };

  // Function to handle manual address lookup
  const handleManualAddressLookup = useCallback(
    async (manualAddress: string) => {
      // Only proceed if we have an address
      if (!manualAddress) return;

      console.log('Attempting manual address lookup for:', manualAddress);

      // Set looking up state
      setIsAddressLookingUp(true);

      // Hide remaining fields while looking up
      setShowRemainingFields(false);

      try {
        // Try to parse the address
        // Expected format: "Street, City, State Zip"
        const parts = manualAddress.split(',');
        if (parts.length < 3) {
          console.log('Invalid address format for manual lookup');
          setIsAddressLookingUp(false);
          setShowRemainingFields(true);
          return;
        }

        const street = parts[0].trim();
        const city = parts[1].trim();
        const stateZipParts = parts[2].trim().split(' ');

        if (stateZipParts.length < 2) {
          console.log('Invalid state/zip format for manual lookup');
          setIsAddressLookingUp(false);
          setShowRemainingFields(true);
          return;
        }

        const state = stateZipParts[0].trim();
        const zip = stateZipParts[1].trim();

        // Check zip code serviceability first
        const isServiceable = await checkZipCodeServiceabilityStatus(zip);

        // Only proceed with customer lookup if zip code is serviceable
        if (!isServiceable) {
          setIsAddressLookingUp(false);
          return;
        }

        // Perform the lookup
        await lookupCustomerByAddress({
          street,
          unit: '',
          city,
          state,
          zip,
        });
      } catch (error) {
        console.error('Error in manual address lookup:', error);
        setIsAddressLookingUp(false);
        setShowRemainingFields(true);
      }
    },
    [lookupCustomerByAddress, checkZipCodeServiceabilityStatus]
  );

  // Customer Confirmation Dialog Component
  const CustomerConfirmationDialog = () => {
    if (!showCustomerConfirmation || !selectedCustomer) return null;

    const nameParts = selectedCustomer.name
      ? selectedCustomer.name.split(' ')
      : ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    const email = selectedCustomer.emails
      ? selectedCustomer.emails.split(',')[0].trim()
      : '';
    const address = selectedCustomer.address
      ? `${selectedCustomer.address.street}${selectedCustomer.address.unit ? ` ${selectedCustomer.address.unit}` : ''}, ${selectedCustomer.address.city}, ${selectedCustomer.address.state} ${selectedCustomer.address.zip}`
      : '';

    const hasMemberships =
      customerMemberships && customerMemberships.length > 0;

    return (
      <div
        className='fixed inset-0 flex items-center justify-center'
        style={{
          zIndex: 1000, // Very high z-index to ensure it's on top
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div
          className='bg-white p-6 rounded-lg shadow-lg max-w-md w-full'
          style={{
            position: 'relative',
            zIndex: 1001, // Even higher z-index for the actual dialog
          }}
        >
          <h3 className='text-lg font-semibold mb-4'>
            Confirm Customer Information
          </h3>
          <div className='mb-4'>
            <p className='mb-2'>
              <strong>Name:</strong> {firstName} {lastName}
            </p>
            <p className='mb-2'>
              <strong>Phone:</strong> {customerInfo.phone}
            </p>
            {email && (
              <p className='mb-2'>
                <strong>Email:</strong> {email}
              </p>
            )}
            {address && (
              <p className='mb-2'>
                <strong>Address:</strong> {address}
              </p>
            )}
            {hasMemberships && (
              <div className='mt-3 bg-blue-50 border border-blue-100 p-2 rounded-md'>
                <p className='text-blue-800 font-medium'>
                  <strong>Membership:</strong> {customerMemberships.join(', ')}
                </p>
              </div>
            )}
          </div>
          <p className='mb-4'>Is this information correct?</p>
          <div className='flex justify-end space-x-3'>
            <button
              className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100'
              onClick={() => {
                setShowCustomerConfirmation(false);
                setShowAddressSelection(false);
                setShowCustomerSelection(false);
                setShowAddressField(true);
                setShowRemainingFields(true); // Show remaining fields for manual entry
                setCustomerMemberships([]); // Clear memberships when rejecting

                // Clear previously selected customer data but keep the address
                if (selectedAddress) {
                  const addressStr = `${selectedAddress.street}${selectedAddress.unit ? ` ${selectedAddress.unit}` : ''}, ${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zip}`;

                  setCustomerInfo((prevInfo) => ({
                    ...prevInfo,
                    firstName: '',
                    lastName: '',
                    email: '',
                    address: addressStr,
                  }));

                  // Keep the location ID but clear the customer ID
                  setCustomerId(null);

                  // Reset validation states
                  setIsEmailValid(false);
                  setIsAddressValid(true);
                }
              }}
            >
              No
            </button>
            <button
              className='px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700'
              onClick={() => {
                setShowCustomerConfirmation(false);
                setShowAddressSelection(false);

                // Set customer and location IDs
                setCustomerId(selectedCustomer.id);
                if (selectedCustomer.address) {
                  setLocationId(selectedCustomer.address.id);
                }

                // Update customer info with the confirmed data
                setCustomerInfo((prevInfo) => ({
                  ...prevInfo,
                  firstName,
                  lastName,
                  email: email || prevInfo.email,
                  address: address || prevInfo.address,
                }));

                // Set email validation state
                if (email) {
                  setIsEmailValid(validateEmail(email));
                }

                // Set address validation state to true since we have a valid address from the lookup
                setIsAddressValid(true);

                // Show all fields since customer was confirmed
                setShowAddressField(true);
                setShowRemainingFields(true);
              }}
            >
              Yes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Address Selection Dialog Component
  const AddressSelectionDialog = () => {
    if (!showAddressSelection || !addressMatches.length) return null;

    return (
      <div
        className='fixed inset-0 flex items-center justify-center'
        style={{
          zIndex: 1000, // Very high z-index to ensure it's on top
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div
          className='bg-white p-6 rounded-lg shadow-lg max-w-md w-full'
          style={{
            position: 'relative',
            zIndex: 1001, // Even higher z-index for the actual dialog
          }}
        >
          <h3 className='text-lg font-semibold mb-4'>Select Your Address</h3>
          <p className='mb-4'>
            We found multiple addresses associated with this phone number.
            Please select your address:
          </p>
          <div className='max-h-60 overflow-y-auto mb-4'>
            {addressMatches.map((address, index) => (
              <div
                key={index}
                className='p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-100'
                onClick={() => {
                  setShowAddressSelection(false);
                  setShowCustomerSelection(false);
                  setShowCustomerConfirmation(false);

                  setSelectedAddress(address);

                  if (address.occupants.length === 1) {
                    // Single occupant at this address
                    const occupant = address.occupants[0];
                    setSelectedCustomer(occupant);

                    // Parse name parts
                    const nameParts = occupant.name
                      ? occupant.name.split(' ')
                      : ['', ''];
                    const firstName = nameParts[0] || '';
                    const lastName = nameParts.slice(1).join(' ') || '';

                    // Get email if available
                    const email = occupant.emails
                      ? occupant.emails.split(',')[0].trim()
                      : '';

                    // Format address
                    const addressStr = `${address.street}${address.unit ? ` ${address.unit}` : ''}, ${address.city}, ${address.state} ${address.zip}`;

                    // Update customer info with the selected data
                    setCustomerInfo((prevInfo) => ({
                      ...prevInfo,
                      firstName,
                      lastName,
                      email: email || prevInfo.email,
                      address: addressStr,
                    }));

                    // Set email validation state
                    if (email) {
                      setIsEmailValid(validateEmail(email));
                    }

                    // Set address validation state to true since we have a valid address from the lookup
                    setIsAddressValid(true);

                    // Set customer and location IDs
                    setCustomerId(occupant.id);
                    setLocationId(address.id);

                    // Handle memberships using the helper function
                    handleCustomerMemberships(occupant, 'address-selection');

                    // Show all fields since customer was selected
                    setShowAddressField(true);
                    setShowRemainingFields(true);
                  } else if (address.occupants.length > 1) {
                    // Multiple occupants at this address
                    // First close this modal completely before showing the customer selection
                    // to ensure any other UI elements have fully updated
                    setShowAddressSelection(false);

                    // Then show the customer selection dialog after the modal has closed
                    setTimeout(() => {
                      setShowCustomerSelection(true);
                    }, 300);
                  }
                }}
              >
                {address.street}
                {address.unit ? ` ${address.unit}` : ''}, {address.city},{' '}
                {address.state} {address.zip}
                <div className='text-sm text-gray-500 mt-1'>
                  {address.occupants.length}{' '}
                  {address.occupants.length === 1 ? 'occupant' : 'occupants'}
                </div>
              </div>
            ))}
            <div
              className='p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-100 bg-gray-50'
              onClick={() => {
                setShowAddressSelection(false);
                setShowCustomerSelection(false);
                setShowCustomerConfirmation(false);
                setShowAddressField(true);
                setShowRemainingFields(false);
                setCustomerId(null);
                setLocationId(null);

                // Clear all customer info for a brand new entry
                setCustomerInfo((prevInfo) => ({
                  ...prevInfo,
                  firstName: '',
                  lastName: '',
                  email: '',
                  address: '',
                }));

                // Reset validation states
                setIsEmailValid(false);
                setIsAddressValid(false);
              }}
            >
              <div className='flex items-center'>
                <span className='mr-2'>+</span> Add New Address
              </div>
            </div>
          </div>
          <div className='flex justify-end'>
            <button
              className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100'
              onClick={() => {
                setShowAddressSelection(false);
                setShowCustomerSelection(false);
                setShowCustomerConfirmation(false);
                setShowAddressField(true);
                setShowRemainingFields(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Customer Selection Dialog Component
  const CustomerSelectionDialog = () => {
    if (
      !showCustomerSelection ||
      !selectedAddress ||
      !selectedAddress.occupants.length
    )
      return null;

    // Sort occupants to prioritize those with memberships
    const sortedOccupants = [...selectedAddress.occupants].sort((a, b) => {
      // Put customers with memberships first
      const aHasMembership =
        (a.memberships && a.memberships.length > 0) || a.isMember;
      const bHasMembership =
        (b.memberships && b.memberships.length > 0) || b.isMember;

      if (aHasMembership && !bHasMembership) return -1;
      if (!aHasMembership && bHasMembership) return 1;
      return 0;
    });

    return (
      <div
        className='fixed inset-0 flex items-center justify-center'
        style={{
          zIndex: 1000, // Very high z-index to ensure it's on top
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div
          className='bg-white p-6 rounded-lg shadow-lg max-w-md w-full'
          style={{
            position: 'relative',
            zIndex: 1001, // Even higher z-index for the actual dialog
          }}
        >
          <h3 className='text-lg font-semibold mb-4'>Select Occupant</h3>
          <p className='mb-4'>
            Multiple people are associated with this address. Who should we
            schedule this appointment for?
          </p>
          <div className='max-h-60 overflow-y-auto mb-4'>
            {sortedOccupants.map((occupant, index) => {
              const hasMembership =
                (occupant.memberships && occupant.memberships.length > 0) ||
                occupant.isMember;

              return (
                <div
                  key={index}
                  className={`p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-100 ${hasMembership ? 'border-blue-300 bg-blue-50' : ''}`}
                  onClick={() => {
                    setShowCustomerSelection(false);
                    setShowAddressSelection(false);
                    setShowCustomerConfirmation(false);

                    // Set the selected customer
                    setSelectedCustomer(occupant);

                    // Update customer info with the selected data
                    setCustomerInfo((prevInfo) => ({
                      ...prevInfo,
                      firstName: occupant.name.split(' ')[0],
                      lastName: occupant.name.split(' ').slice(1).join(' '),
                      email: occupant.emails
                        ? occupant.emails.split(',')[0].trim()
                        : '',
                    }));

                    // Set email validation state
                    if (occupant.emails) {
                      setIsEmailValid(
                        validateEmail(occupant.emails.split(',')[0].trim())
                      );
                    }

                    // Set address validation state to true since we have a valid address from the lookup
                    setIsAddressValid(true);

                    // Set customer and location IDs
                    setCustomerId(occupant.id);
                    if (occupant.address) {
                      setLocationId(occupant.address.id);
                    }

                    // Handle memberships using the helper function
                    handleCustomerMemberships(occupant, 'customer-selection');

                    // Show all fields since customer was confirmed
                    setShowAddressField(true);
                    setShowRemainingFields(true);
                  }}
                >
                  <div className='flex items-center justify-between'>
                    <div>
                      {occupant.name}
                      {occupant.phone && (
                        <div className='text-sm text-gray-500'>
                          {occupant.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div
              className='p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-100 bg-gray-50'
              onClick={() => {
                console.log(
                  'Creating new profile at existing address:',
                  selectedAddress
                );

                setShowCustomerSelection(false);
                setShowCustomerConfirmation(false);
                setShowAddressSelection(false);

                if (selectedAddress) {
                  setLocationId(selectedAddress.id);
                }
                setCustomerId(null);

                // Keep the address but clear the name and email
                const addressStr = `${selectedAddress.street}${selectedAddress.unit ? ` ${selectedAddress.unit}` : ''}, ${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zip}`;
                setCustomerInfo((prevInfo) => ({
                  ...prevInfo,
                  firstName: '',
                  lastName: '',
                  email: '',
                  address: addressStr,
                }));

                // Set email validation state
                setIsEmailValid(false);

                // Set address validation state to true since we have a valid address from the lookup
                setIsAddressValid(true);

                // Show all fields since customer was confirmed
                setShowAddressField(true);
                setShowRemainingFields(true);
              }}
            >
              <div className='flex items-center'>
                <span className='mr-2'>+</span> Create New Profile
              </div>
            </div>
          </div>
          <div className='flex justify-end'>
            <button
              className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100'
              onClick={() => {
                setShowCustomerSelection(false);
                setShowCustomerConfirmation(false);
                setShowAddressSelection(false);
                setShowAddressField(true);
                setShowRemainingFields(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Update useEffect for initialization
  useEffect(() => {
    // Simulate initial loading with a timeout
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Add a utility function to sort availabilities chronologically
  const sortAvailabilitiesByDate = (
    availabilities: Availability[]
  ): Availability[] => {
    const sorted = [...availabilities].sort((a, b) => {
      const aDate = new Date(a.startDate || a.startUtc || a.start);
      const bDate = new Date(b.startDate || b.startUtc || b.start);

      return aDate.getTime() - bDate.getTime();
    });

    return sorted;
  };

  // Handle setting initial calendar date from availabilities
  const updateCalendarWithEarliestDate = (
    sortedAvailabilities: Availability[]
  ) => {
    if (sortedAvailabilities[0]?.startDate) {
      try {
        const firstAvailableDate = new Date(sortedAvailabilities[0].startDate);
        if (!isNaN(firstAvailableDate.getTime())) {
          setCurrentDate(firstAvailableDate);
        }
      } catch (e) {
        // Silently handle errors without crashing
      }
    }
  };

  // Add a function to handle additional question answers
  const handleAdditionalQuestionAnswer = (
    questionId: string,
    optionId: string
  ) => {
    setSystemInfo((prevState) => ({
      ...prevState,
      additionalAnswers: {
        ...prevState.additionalAnswers,
        [questionId]: optionId,
      },
    }));
  };

  // Add useEffect to clean up answers for questions that don't apply when selectedIssueType changes
  useEffect(() => {
    if (selectedIssueType && additionalQuestions.length > 0) {
      // Clone current answers
      const updatedAnswers = { ...systemInfo.additionalAnswers };

      // Find questions that don't apply to current selection
      additionalQuestions.forEach((question) => {
        if (
          question.applicableIssueTypes &&
          !question.applicableIssueTypes.includes(selectedIssueType)
        ) {
          // Remove answer for this question
          delete updatedAnswers[question.id];
        }
      });

      // Update state with filtered answers
      setSystemInfo((prev) => ({
        ...prev,
        additionalAnswers: updatedAnswers,
      }));
    }
  }, [selectedIssueType, additionalQuestions]);

  return (
    <div style={containerStyles}>
      {isInitialLoading ? (
        <AnimatePresence>
          <motion.div
            key='initial-loading'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              width: '100%',
              padding: '20px',
            }}
          >
            {/* Truck Icon */}
            <div
              style={{
                position: 'relative',
                width: '120px',
                height: '120px',
                marginBottom: '30px',
              }}
            >
              {/* Road */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '10px',
                  width: '100%',
                  height: '4px',
                  backgroundColor: '#ccc',
                  borderRadius: '2px',
                }}
              />

              {/* Truck */}
              <motion.div
                style={{
                  position: 'relative',
                  width: '60px',
                  height: '35px',
                }}
                initial={{ x: 0 }}
                animate={{ x: 60 }}
                transition={{
                  repeat: Infinity,
                  repeatType: 'reverse',
                  duration: 1.5,
                  ease: 'easeInOut',
                }}
              >
                {/* Truck Body */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '15px',
                    width: '40px',
                    height: '20px',
                    backgroundColor: primaryColor,
                    borderRadius: '3px 0 0 3px',
                  }}
                />

                {/* Truck Cabin */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '15px',
                    left: '40px',
                    width: '20px',
                    height: '16px',
                    backgroundColor: primaryColor,
                    borderRadius: '0 3px 0 0',
                  }}
                />

                {/* Wheels */}
                <motion.div
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '10px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#333',
                    borderRadius: '50%',
                  }}
                />

                <motion.div
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '42px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#333',
                    borderRadius: '50%',
                  }}
                />
              </motion.div>
            </div>

            {/* Pulsing Circle Animations */}
            <motion.div
              style={{
                width: '100px',
                height: '5px',
                backgroundColor: `${primaryColor}40`,
                borderRadius: '6px',
                marginBottom: '20px',
              }}
              animate={{ scaleX: [0.3, 0.7, 0.3] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              style={{
                fontSize: '22px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '12px',
                textAlign: 'center',
              }}
            ></motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              style={{
                fontSize: '16px',
                color: '#666',
                maxWidth: '280px',
                textAlign: 'center',
              }}
            >
              Preparing your scheduling assistant...
            </motion.div>
          </motion.div>
        </AnimatePresence>
      ) : (
        <>
          {!showSuccessAnimation && (
            <ProgressTimeline
              currentStage={currentStage}
              stages={stages}
              stageNames={stageNames}
              primaryColor={primaryColor}
            />
          )}
          {renderStage()}
          {CustomerConfirmationDialog()}
          {AddressSelectionDialog()}
          {CustomerSelectionDialog()}
        </>
      )}
      {showServiceableMessage && (
        <AnimatePresence>
          <motion.div
            key='serviceable-message'
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            style={{
              marginTop: '8px',
              padding: '6px 10px',
              backgroundColor: '#ecfdf5',
              border: '1px solid #10b981',
              borderRadius: '6px',
              color: '#10b981',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle cx='8' cy='8' r='7' stroke='#10b981' strokeWidth='1.5' />
              <path
                d='M5 8L7 10L11 6'
                stroke='#10b981'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span>Serviceable address confirmed</span>
          </motion.div>
        </AnimatePresence>
      )}
      {isZipServiceable === false &&
        nonServiceableZipCode &&
        currentStage === 'info' && (
          <AnimatePresence>
            <motion.div
              key='non-serviceable-message'
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#fee2e2',
                borderRadius: '8px',
                color: '#991b1b',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '18px' }}>⚠️</span>
                <div>
                  <p
                    style={{
                      margin: '0 0 8px 0',
                      fontWeight: '500',
                    }}
                  >
                    Out of Service Area
                  </p>
                  <p style={{ margin: '0', fontSize: '14px' }}>
                    We&apos;re sorry, but we don&apos;t currently service the
                    zip code <strong>{nonServiceableZipCode}</strong>. Please
                    call us directly for more information or to check if we can
                    make special arrangements.
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      {/* Logo and Powered by Avoca footer */}
      <div
        style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          marginBottom: '0.5rem',
          fontSize: '0.7rem',
          color: '#6b7280',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          width: '100%',
        }}
      >
        {logoUrl && (
          <img
            src={logoUrl}
            alt='Company Logo'
            style={{
              width: logoUrl.includes('winters-hlbowman.png')
                ? '280px'
                : '180px',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto',
            }}
          />
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            justifyContent: 'center',
            width: '100%',
            marginTop: '-5px',
          }}
        >
          Powered by{' '}
          <a
            href='/demo'
            style={{
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              color: '#6b7280',
            }}
            target='_blank'
            rel='noopener noreferrer'
          >
            Avoca
          </a>
          <span
            style={{
              display: 'inline-block',
              transform: 'scale(0.4)',
              marginLeft: '-4px',
            }}
          >
            <Logo logoClassName='scale-[0.90] -ml-1' variant='logo' />
          </span>
        </div>
      </div>
    </div>
  );
};

export { EnhancedSchedulingFlow };
